if (window.location.pathname.startsWith("/block")) {

// ── Config ────────────────────────────────────────────────
const PALAMSEL_DATA  = "/static/palamsel_map/data/";
// NOTE: drone tiles come from the shared EstateMap module (modules/map.js).

const PALM_CONFIG = {
    palm_normal:      { color: '#22c55e', label: 'Normal Palms',  emoji: '🌴', prop: '#NormalPalm' },
    palm_abnormal:    { color: '#ef4444', label: 'Abnormal',      emoji: '⚠️', prop: '#AbnormalPalm' },
    palm_tothin:      { color: '#f59e0b', label: 'To Thin',       emoji: '✂️', prop: '#ToThin' },
    palm_overplanted: { color: '#a855f7', label: 'Overplanted',   emoji: '🔴', prop: '#OverplantedPalm' },
    palm_coconut:     { color: '#0ea5e9', label: 'Coconut',       emoji: '🥥', prop: '#Coconut' },
};

// Toggleable overlay road layers (lazy-loaded from the GeoJSON on first enable).
// `mult` scales the zoom-dependent base weight so roads stay readable at every zoom
// (thin when zoomed out, thick when zoomed in) instead of a fixed pixel width.
const ROAD_CONFIG = {
    road_main:       { label: 'Main Roads',       color: '#e05050', mult: 1.0 },
    road_connecting: { label: 'Connecting Roads', color: '#f0c040', mult: 0.7 },
};

// Base line weight from the current zoom: ~1px at z14 → ~7px at z21.
function roadWeight(mult) {
    const z = blockmap ? blockmap.getZoom() : 16;
    const base = Math.max(1, Math.min(7, (z - 13) * 1.1));
    return base * mult;
}

// Block border weight scaled by zoom (thin when zoomed out, thicker when zoomed in).
// `mult` lets each block state (normal/active/dim) be relatively thicker or thinner.
function blockWeight(mult) {
    const z = blockmap ? blockmap.getZoom() : 16;
    const base = Math.max(1, Math.min(5, (z - 13) * 0.8));
    return base * mult;
}

// `wmult` = relative border thickness; the real weight is blockWeight(wmult), zoom-scaled.
const BLOCK_STYLE        = { color: '#000000', wmult: 1.0, fillColor: '#2ecc71', fillOpacity: 0.08, opacity: 0.7 };
const BLOCK_STYLE_ACTIVE = { color: '#ffffff', wmult: 1.2, fillColor: '#2ecc71', fillOpacity: 0.08, opacity: 1 };
const BLOCK_STYLE_DIM    = { color: '#00ff6a', wmult: 0.5, fillColor: '#000000', fillOpacity: 0.45, opacity: 0.35 };

// Resolve a block style + its zoom-scaled weight into a Leaflet style object.
function blockStyle(s) {
    return { ...s, weight: blockWeight(s.wmult) };
}

// ── State ─────────────────────────────────────────────────
let blockmap;
let blockLayer;
let activeLayer = null;
let activeBlockId = null;
const layerByBlockId = {};

const activePalmLayers = {}; // name -> leaflet layer (for current block)
let currentPalmCode = null;
let palmRenderer = null;     // created in initBlockMap, in a high-z pane so palms sit above the sub-block fill

const roadPromise = {};      // name -> fetch promise
const roadLayers = {};       // name -> leaflet layer (estate-wide)

// Sub-block (task-area) state
let sectionLayer = null;        // leaflet layer for the current block's sub-blocks
let previewFeatures = null;     // last preview FeatureCollection (unsaved)
const SECTION_PALETTE = ['#ef4444','#f59e0b','#22c55e','#0ea5e9','#a855f7','#ec4899','#14b8a6','#eab308','#3b82f6','#f97316','#84cc16','#06b6d4','#d946ef','#10b981','#f43f5e','#8b5cf6','#fbbf24','#f87171','#34d399','#60a5fa','#c084fc','#fb923c','#4ade80','#22d3ee','#e879f9'];

let basemaps = {};   // drone basemap comes from the shared EstateMap module

// ── Helpers ───────────────────────────────────────────────
function fmt(v, d = 1) {
    if (v === null || v === undefined || v === '') return '—';
    return typeof v === 'number' ? v.toFixed(d) : v;
}
function fmtInt(v) {
    if (v === null || v === undefined || v === '') return '—';
    return Number(v).toLocaleString();
}
function blockProps(feature) { return feature.properties || {}; }
// "Block <name>" for labels (the name can be changed in the editor; it starts as the survey code).
function blockLabel(feature) { return `Block ${feature.name || feature.code || blockProps(feature).Block || '?'}`; }

// ── Map init ──────────────────────────────────────────────
function initBlockMap() {
    blockmap = L.map('map', { center: [5.408, 102.222], zoom: 14, maxZoom: 21 });

    // Palms render in a dedicated pane ABOVE the sub-block fill + roads (overlayPane z=400).
    // pointer-events:none keeps them purely visual so they never block sub-block / pen clicks.
    blockmap.createPane('palms');
    const _palmsPane = blockmap.getPane('palms');
    _palmsPane.style.zIndex = 450;
    _palmsPane.style.pointerEvents = 'none';
    palmRenderer = L.canvas({ padding: 0.5, pane: 'palms' });
    // Tree-management markers: same look as the palm dots but CLICKABLE, in their own pane on top.
    blockmap.createPane('trees');
    blockmap.getPane('trees').style.zIndex = 460;
    treeRenderer = L.canvas({ padding: 0.5, pane: 'trees' });

    // Drone-ortho + satellite basemap from the shared EstateMap module.
    EstateMap.addBasemap(blockmap).then(() => {
        if (blockLayer) blockLayer.eachLayer(l => l.bringToFront());
    });

    blockLayer = L.geoJSON(blocks, {
        style: () => blockStyle(BLOCK_STYLE),
        onEachFeature: (feature, layer) => {
            layerByBlockId[feature.id] = layer;
            layer.bindTooltip(blockLabel(feature), { sticky: true, className: 'nh-hover-tip' });
            layer.on('mouseover', function () { if (this !== activeLayer && !activeLayer) this.setStyle({ fillOpacity: 0.18, weight: blockWeight(1.4) }); });
            layer.on('mouseout',  function () { if (this !== activeLayer && !activeLayer) this.setStyle(blockStyle(BLOCK_STYLE)); });
            layer.on('click', function (e) {
                L.DomEvent.stopPropagation(e);
                if (penMode) { addPenPoint(e.latlng); return; }   // place a pen point instead of switching blocks
                if (treeMode && this === activeLayer) { addTree(e.latlng); return; }   // tree management: drop a tree in the active block
                if (rowMode && this === activeLayer) return;      // row mapping: only tree clicks build a row
                // (tree mode + another block → falls through to selectBlock, which asks about unsaved edits and ends tree mode)
                if (cutMode || eraseMode) return;                 // a draw tool is active → don't change the selection
                if (this === activeLayer) closeDetail();
                else selectBlock(feature, this);
            });
        }
    }).addTo(blockmap);

    fitEstate();

    blockmap.on('click', (e) => {
        if (penMode) { addPenPoint(e.latlng); return; }   // pen tool: each click drops an anchor point
        if (treeMode || rowMode) {
            // The tree canvas covers the polygons, so their click handlers never fire here:
            // resolve the click by geometry instead (a tree marker handles its own click).
            if (pointInBlock(e.latlng)) {
                if (treeMode) addTree(e.latlng);
                else setRowHint('Click a tree to connect it to the row.');
                return;
            }
            const other = blockAt(e.latlng);
            if (other && layerByBlockId[other.id]) selectBlock(other, layerByBlockId[other.id]);
            else if (treeMode) setTreeHint('⚠️ Click inside the block boundary to add a tree.');
            return;
        }
        if (eraseMode || cutMode) return;
        
        // If the tree canvas intercepts the click (e.g. rowsVisible is true), 
        // Leaflet forwards it to the map. We must manually resolve it to a block.
        const other = blockAt(e.latlng);
        if (other) {
            if (activeBlockId !== other.id && layerByBlockId[other.id]) {
                selectBlock(other, layerByBlockId[other.id]);
                return;
            } else if (activeBlockId === other.id) {
                if (!editing) closeDetail();
                return;
            }
        }
        
        if (activeLayer && !editing) closeDetail();
    });
    // Pen tool: rubber-band a preview segment from the last point to the cursor.
    blockmap.on('mousemove', (e) => {
        if (!penMode || spacePan || !cutPath.length) return;
        const last = cutPath[cutPath.length - 1];
        const seg = [[last[1], last[0]], [e.latlng.lat, e.latlng.lng]];
        if (!penRubber) penRubber = L.polyline(seg, { color: '#2563eb', weight: 2, dashArray: '4 4', opacity: 0.7 }).addTo(blockmap);
        else penRubber.setLatLngs(seg);
    });
    // Pen tool: right-click removes the last placed point.
    blockmap.on('contextmenu', (e) => {
        if (!penMode) return;
        L.DomEvent.preventDefault(e);
        penUndo();
    });
    // Hold SPACE to temporarily pan the map while a draw tool (cut / erase / pen) is active.
    document.addEventListener('keydown', (ev) => {
        const t = ev.target; if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
        // Backspace/Delete steps back one pen point while drawing.
        if (penMode && (ev.code === 'Backspace' || ev.code === 'Delete')) { ev.preventDefault(); penUndo(); return; }
        // Escape: first cancels an active draw tool, otherwise leaves the full-screen editor.
        if (ev.code === 'Escape' && editing) {
            if (cutMode || eraseMode || penMode) { endCut(); endErase(); endPen(); }
            else if (rowMode && rowDraft.length) clearRowDraft();
            else if (rowMode) exitRowMode();
            else if (treeMode && treeSelected) selectTree(null);
            else if (treeMode) exitTreeMode();
            else exitEditMode();
            return;
        }
        if (ev.code !== 'Space' || spacePan) return;
        if (!(cutMode || eraseMode || penMode)) return;
        ev.preventDefault();                         // stop the page from scrolling
        spacePan = true;
        blockmap.dragging.enable();
        blockmap._container.style.cursor = 'grab';
    });
    document.addEventListener('keyup', (ev) => {
        if (ev.code !== 'Space' || !spacePan) return;
        spacePan = false;
        // Restore the active tool: pen keeps drag enabled; freehand cut/erase re-lock it.
        if (penMode) blockmap.dragging.enable();
        else if (cutMode || eraseMode) blockmap.dragging.disable();
        blockmap._container.style.cursor = (cutMode || eraseMode || penMode) ? 'crosshair' : '';
    });

    // Erase/cut draw via DOM-container events so they fire reliably ANYWHERE on the map
    // (even when the press starts on a sub-block polygon) and never trigger map drag.
    const _container = blockmap.getContainer();
    const _ll = (ev) => blockmap.mouseEventToLatLng(ev);
    _container.addEventListener('mousedown', (ev) => {
        if (spacePan) return;                 // SPACE held → let Leaflet pan instead of drawing
        if (!eraseMode && !cutMode) return;
        ev.preventDefault(); ev.stopPropagation();
        const ll = _ll(ev);
        if (eraseMode) { erasing = true; brushPath.push([ll.lng, ll.lat]); }
        else if (cutMode) { cutting = true; cutPath = [[ll.lng, ll.lat]]; if (cutLine) { blockmap.removeLayer(cutLine); cutLine = null; } updateCutApply(); }
    }, true);
    _container.addEventListener('mousemove', (ev) => {
        if (spacePan) return;
        if (!eraseMode && !cutMode) return;
        const ll = _ll(ev);
        if (eraseMode) {
            if (!brushCursor) brushCursor = L.circle(ll, { radius: brushM, color: '#ff3b3b', weight: 1, fillColor: '#ff3b3b', fillOpacity: 0.25 }).addTo(blockmap);
            else brushCursor.setLatLng(ll);
            if (erasing) brushPath.push([ll.lng, ll.lat]);
        } else if (cutMode && cutting) {
            cutPath.push([ll.lng, ll.lat]);
            const latlngs = cutPath.map(p => [p[1], p[0]]);
            if (!cutLine) cutLine = L.polyline(latlngs, { color: '#2563eb', weight: 3, dashArray: '5 4' }).addTo(blockmap);
            else cutLine.setLatLngs(latlngs);
        }
    }, true);
    _container.addEventListener('mouseup', () => {
        if (spacePan) return;
        if (eraseMode && erasing) { erasing = false; if (brushPath.length) commitErase(); }
        // Cut: keep the drawn line as a pending mask — the cut is applied explicitly via the button.
        else if (cutMode && cutting) { cutting = false; finalizeCutStroke(); }
    }, true);

    // CSS (.blocks-page flex layout in map.scss) controls the map height.
    // Leaflet needs to recalc its container size after layout settles / on resize,
    // then re-fit so the estate fills the final container (not the pre-layout size).
    setTimeout(() => { blockmap.invalidateSize(); if (!activeLayer) fitEstate(); updateTipVisibility(); }, 150);
    window.addEventListener('resize', () => { blockmap.invalidateSize(); if (!activeLayer) fitEstate(); });
}

// Fit the estate by HEIGHT: its top & bottom align with the container edges
// (no vertical gap), letting the wider axis extend past the sides naturally.
function fitEstate() {
    if (!blockLayer) return;
    try {
        const b = blockLayer.getBounds();
        if (!b.isValid()) return;
        const size = blockmap.getSize();
        const pad = 16; // small breathing room top/bottom
        // Pixel span of the bounds' vertical extent at the current zoom...
        const nw = blockmap.project(b.getNorthWest());
        const sw = blockmap.project(b.getSouthWest());
        const boundsPxHeight = Math.abs(sw.y - nw.y) || 1;
        // ...scaled so it fills (container height - padding); convert ratio → zoom delta.
        const scale = (size.y - pad * 2) / boundsPxHeight;
        const z = blockmap.getZoom() + Math.log2(scale);
        blockmap.setView(b.getCenter(), z, { animate: false });
    } catch (e) {}
}

// ── Select / deselect ─────────────────────────────────────
function selectBlock(feature, layer) {
    // (Re)selecting a block reloads its saved sections — ask before losing unsaved sub-block edits.
    if (!confirmDiscard()) return;
    
    // Clear out any active drawing modes (row mapping, etc.) and state from the old block
    if (activeBlockId && activeBlockId !== feature.id) {
        resetSectionState();
    }
    
    const props = blockProps(feature);
    const code = feature.code || props.Block || '?';

    blockLayer.eachLayer(l => {
        if (l === layer) { l.setStyle(blockStyle(BLOCK_STYLE_ACTIVE)); l.bringToFront(); }
        else l.setStyle(blockStyle(BLOCK_STYLE_DIM));
    });
    activeLayer = layer;
    activeBlockId = feature.id;
    currentPalmCode = String(code);

    // Dim road segments that are NOT in the focused block; keep the block's own roads bright.
    applyRoadFocus(code);

    // dropdown active state + label
    $('.block-row').removeClass('bg-slate-100');
    $(`.block-row[data-block-id="${feature.id}"]`).addClass('bg-slate-100');
    $('#blockDropdownLabel').text(blockLabel(feature));
    closeBlockDropdown();

    $('#selectedBlockLabel').text(blockLabel(feature));
    syncUrl(false);

    zoomToActiveBlock();
    clearPalmLayers();
    openDetail(feature);
}

// Zoom tightly to the active block. Reserve space on the right for the detail panel
// (so the focused block isn't hidden under it on wider screens).
function zoomToActiveBlock() {
    if (!activeLayer) return;
    try {
        const panelW = window.innerWidth >= 640 ? 320 : 0; // sm:w-80
        blockmap.fitBounds(activeLayer.getBounds(), {
            paddingTopLeft: [40, 40],
            paddingBottomRight: [panelW + 40, 40],
            maxZoom: 19,
        });
    } catch (e) {}
}

// Re-fit the map to whatever is in focus (used after the container changes size).
function refitMap() {
    if (!blockmap) return;
    blockmap.invalidateSize();
    if (activeLayer) zoomToActiveBlock(); else fitEstate();
}

// True when it is safe to drop the working set (nothing unsaved, or the user agreed to discard).
function confirmDiscard() {
    if (!dirty && !treeDirty() && !rowsDirty()) return true;
    return window.confirm('You have unsaved changes (sub-blocks / trees / rows). Discard them?');
}

function closeDetail() {
    if (!confirmDiscard()) return;
    blockLayer.eachLayer(l => l.setStyle(blockStyle(BLOCK_STYLE)));
    applyRoadFocus(null); // restore all roads to normal
    activeLayer = null;
    activeBlockId = null;
    currentPalmCode = null;
    $('.block-row').removeClass('bg-slate-100');
    $('#selectedBlockLabel').text('');
    $('#blockDropdownLabel').text('Select a block');
    clearPalmLayers();
    resetSectionState();
    const panel = document.getElementById('blockDetailPanel');
    if (panel) panel.classList.add('translate-x-full');
    syncUrl(false);
}

// ── Block dropdown ────────────────────────────────────────
function openBlockDropdown()  { document.getElementById('blockDropdownMenu').classList.remove('hidden'); $('#blockSearch').val('').trigger('input').focus(); }
function closeBlockDropdown() { const m = document.getElementById('blockDropdownMenu'); if (m) m.classList.add('hidden'); }
function toggleBlockDropdown() {
    const m = document.getElementById('blockDropdownMenu');
    if (m.classList.contains('hidden')) openBlockDropdown(); else closeBlockDropdown();
}

// ── Palm points (per block, lazy) ─────────────────────────

function clearPalmLayers() {
    Object.values(activePalmLayers).forEach(l => { if (blockmap.hasLayer(l)) blockmap.removeLayer(l); });
    Object.keys(activePalmLayers).forEach(k => delete activePalmLayers[k]);
    $('.palm-toggle-btn').removeClass('on');
}

// ── Road overlay layers (settings dropdown) ───────────────
function fetchRoad(name) {
    if (!roadPromise[name]) {
        roadPromise[name] = fetch(PALAMSEL_DATA + name + '.geojson')
            .then(r => r.json())
            .catch(err => { console.warn('Road load failed', name, err); return null; });
    }
    return roadPromise[name];
}

// Focus mode for roads: segments inside `code` stay bright; all others are dimmed.
// Pass null to reset every road segment back to its normal style.
function applyRoadFocus(code) {
    Object.keys(roadLayers).forEach(name => {
        const cfg = ROAD_CONFIG[name];
        const lyr = roadLayers[name];
        if (!cfg || !lyr) return;
        lyr.eachLayer(seg => {
            const inBlock = code != null && String(seg.feature?.properties?.Block) === String(code);
            if (code == null || inBlock) {
                seg.setStyle({ color: cfg.color, weight: roadWeight(cfg.mult), opacity: 0.9 });
                if (inBlock) seg.bringToFront();
            } else {
                seg.setStyle({ opacity: 0.35, weight: roadWeight(cfg.mult) });
            }
        });
    });
    raiseSelectionOutlines();   // keep the selection OUTLINE above the (re-fronted) roads
}

// The selected sub-block's blue OUTLINE overlays the roads while its fill stays underneath —
// drawn as a separate fill-less line layer so only the border sits on top of the roads.
function setSelectionOutline(wid, on) {
    if (secOutlines[wid]) { blockmap.removeLayer(secOutlines[wid]); delete secOutlines[wid]; }
    if (!on) return;
    const f = workFC && workFC.features.find(x => x.__wid === wid);
    if (!f) return;
    secOutlines[wid] = L.geoJSON(f.geometry, {
        interactive: false,
        style: { color: '#2563eb', weight: 3.5, opacity: 1, fill: false }
    }).addTo(blockmap);
    secOutlines[wid].bringToFront();
}
function raiseSelectionOutlines() {
    Object.values(secOutlines).forEach(l => { if (l && l.bringToFront) l.bringToFront(); });
}
function clearSelectionOutlines() {
    Object.keys(secOutlines).forEach(wid => { blockmap.removeLayer(secOutlines[wid]); delete secOutlines[wid]; });
}

function toggleRoad(name, on) {
    const cfg = ROAD_CONFIG[name];
    if (!cfg) return;

    if (!on) {
        if (roadLayers[name] && blockmap.hasLayer(roadLayers[name])) blockmap.removeLayer(roadLayers[name]);
        return;
    }

    // already loaded → just re-add (respect current block focus)
    if (roadLayers[name]) { roadLayers[name].addTo(blockmap); applyRoadFocus(currentPalmCode); return; }

    fetchRoad(name).then(gj => {
        if (!gj) return;
        roadLayers[name] = L.geoJSON(gj, {
            style: { color: cfg.color, weight: roadWeight(cfg.mult), opacity: 0.9, className: 'road-overlay' },
            onEachFeature: (feature, layer) => {
                const p = feature.properties || {};
                layer.bindTooltip(
                    `${cfg.label} #${p.RoadNumber ?? ''} — Block ${p.Block ?? ''}<br>${Math.round(p.Length_m || 0)} m`,
                    { sticky: true, className: 'nh-hover-tip' }
                );
            }
        }).addTo(blockmap);
        applyRoadFocus(currentPalmCode);
    });
}

// Category layers are drawn from the block's live trees (the same list the inventory counts),
// so a tree you just added shows up here too — and a removed one disappears.
function togglePalm(name) {
    const btn = document.getElementById(`palm-btn-${name}`);
    if (activePalmLayers[name]) {                       // on → off
        blockmap.removeLayer(activePalmLayers[name]);
        delete activePalmLayers[name];
        if (btn) btn.classList.remove('on');
        return;
    }
    if (btn) btn.classList.add('on');
    if (!secBlockFeature) return;
    loadTreeBase(secBlockFeature)
        .then(() => { if (btn && btn.classList.contains('on')) drawPalmLayer(name); })
        .catch(() => { if (btn) btn.classList.remove('on'); });
}

// Draw (or redraw) one category layer from the current tree list.
function drawPalmLayer(name) {
    const btn = document.getElementById(`palm-btn-${name}`);
    if (activePalmLayers[name]) { blockmap.removeLayer(activePalmLayers[name]); delete activePalmLayers[name]; }
    const cfg = PALM_CONFIG[name];
    const r = Math.max(3, Math.min(7, blockmap.getZoom() - 12));
    const lyr = L.layerGroup();
    treeList().forEach(t => {
        if (t.category !== name || t.status !== 'active') return;
        L.circleMarker([t.lat, t.lng], { renderer: palmRenderer, radius: r,
            fillColor: cfg.color, fillOpacity: 1, color: '#fff', weight: 1 }).addTo(lyr);
    });
    if (!lyr.getLayers().length) {
        if (btn) { btn.classList.remove('on'); btn.title = 'No trees in this category'; }
        return;
    }
    lyr.addTo(blockmap);
    activePalmLayers[name] = lyr;
}

// Keep the visible category layers in step with a tree add / remove / delete / undo / save.
function refreshPalmLayers() {
    if (!treeBase) return;
    Object.keys(activePalmLayers).forEach(drawPalmLayer);
}

// ── Detail drawer content ─────────────────────────────────
function palmRow(name, props, total, counts) {
    const cfg = PALM_CONFIG[name];
    const count = counts ? counts[name] : (props[cfg.prop] || 0);
    const pct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
    const disabled = count ? '' : 'opacity-40 pointer-events-none';
    return `
      <div class="py-1.5">
        <div class="flex items-center gap-2">
          <span class="text-sm text-slate-600 flex-1">${cfg.emoji} ${cfg.label}</span>
          <span class="text-sm font-semibold" style="color:${cfg.color}">${fmtInt(count)}</span>
          <span class="text-xs text-slate-400 w-10 text-right">${pct}%</span>
          <button id="palm-btn-${name}" data-palm="${name}"
            type="button" class="palm-toggle-btn layer-toggle ${disabled}"
            aria-label="Toggle ${cfg.label}"></button>
        </div>
        <div class="block-detail-bar mt-1"><span style="width:${pct}%;background:${cfg.color}"></span></div>
      </div>`;
}

function slopeRow(label, pct) {
    if (pct === null || pct === undefined || pct === '') return '';
    const v = parseFloat(pct) || 0;
    const color = v < 20 ? '#22c55e' : v < 40 ? '#f59e0b' : '#ef4444';
    return `
      <div class="py-1">
        <div class="flex justify-between text-xs text-slate-500"><span>${label}</span><span>${v.toFixed(1)}%</span></div>
        <div class="block-detail-bar mt-0.5"><span style="width:${Math.min(100, v)}%;background:${color}"></span></div>
      </div>`;
}

function statRow(label, value, cls = '') {
    return `<div class="flex justify-between py-1"><span class="text-sm text-slate-500">${label}</span><span class="text-sm font-medium text-slate-800 ${cls}">${value}</span></div>`;
}

// A General-card value the editor can change in place: its pencil opens an inline input
// (same controls as the sub-block edit) that saves straight away via /api/block/update.
function editableStatRow(field, label, value, inputHtml, note = '') {
    if (!editing) return statRow(label, value);
    return `
            <div class="dp-edit py-1" data-field="${field}">
              <div class="flex justify-between items-center">
                <span class="text-sm text-slate-500">${label}</span>
                <span class="flex items-center gap-2">
                  <span class="dp-edit-val text-sm font-medium text-slate-800">${value}</span>
                  <button type="button" class="dp-edit-btn text-slate-300 hover:text-blue-600 text-sm leading-none shrink-0" title="Edit ${label.toLowerCase()}"><i class="bi bi-pencil"></i></button>
                </span>
              </div>${note ? `
              <div class="text-[11px] text-slate-400 text-right">${note}</div>` : ''}
              <div class="dp-edit-form hidden mt-2 items-center gap-1.5">
                ${inputHtml}
                <button type="button" class="dp-edit-save inline-flex items-center justify-center h-7 w-7 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-sm leading-none shrink-0" title="Save"><i class="bi bi-check-lg"></i></button>
                <button type="button" class="dp-edit-cancel inline-flex items-center justify-center h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm leading-none shrink-0" title="Cancel"><i class="bi bi-x-lg"></i></button>
              </div>
            </div>`;
}

function buildDetailHTML(feature) {
    const p = blockProps(feature);
    const code = feature.code || p.Block || '?';
    const areaHa = feature.area_ha != null ? feature.area_ha : (p.GrossArea_ha || 0);
    const areaAcre = feature.area_acre != null ? feature.area_acre : (areaHa * 2.47105);
    const blockName = feature.name || code;
    const nameInput = `<input class="dp-edit-input flex-1 min-w-0 border border-slate-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" value="${String(blockName).replace(/"/g, '&quot;')}">`;
    const haInput = v => `<input type="number" step="0.01" min="0.01" class="dp-edit-input flex-1 min-w-0 border border-slate-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" value="${Number(v || 0).toFixed(2)}">
                <span class="text-xs text-slate-500">ha</span>`;
    // Planted = Gross − Main Road − Unplanted (server rule), unless set by hand in the editor.
    const plantedHa = feature.planted_area != null ? feature.planted_area : p.PlantedArea_ha;
    const plantedNote = feature.planted_manual
        ? `<span class="font-medium">edited</span> · <button type="button" class="dp-planted-reset text-blue-600 hover:underline">Use formula</button>`
        : `${fmt(areaHa, 2)} − ${fmt(p.MainRoad_ha || 0, 2)} main road − ${fmt(p.VacantArea_ha || 0, 2)} unplanted`;
    const general = `
            ${editing ? editableStatRow('name', 'Name', blockName, nameInput) : ''}
            ${editableStatRow('area_ha', 'Gross Area', `${fmt(areaHa, 2)} Ha · ${fmt(areaAcre, 2)} Acre`, haInput(areaHa))}
            ${editableStatRow('planted_ha', 'Planted Area', `${fmt(plantedHa, 2)} Ha`, haInput(plantedHa), plantedNote)}
            ${statRow('Vacant Area', `${fmt(p.VacantArea_ha, 2)} Ha`, 'text-amber-600')}
            ${statRow('Planting Year', p['Planting Year'] || '—')}`;

    // /block/all keeps the plain stacked layout it has always had.
    if (!editing) {
        return `
      <div class="space-y-5 text-sm" data-block-code="${code}">
        <div>
          <h4 class="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 border-b border-slate-100 pb-1">General</h4>
          ${general}
        </div>
        <div id="dpInventory">${buildInventoryHTML(feature)}</div>
      </div>`;
    }

    // The editor shows the same data as collapsible cards, matching the Edit block cards.
    return `
      <div class="space-y-2.5" data-block-code="${code}">
        <div class="pb-0.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">Block details</div>
        <div class="rounded-lg border border-slate-200 overflow-hidden">
          ${accHead('dpAccGeneralBody', '<i class="bi bi-graph-up text-emerald-700"></i>', 'General', `${fmt(areaHa, 2)} Ha · ${fmt(plantedHa, 2)} planted`, true)}
          <div id="dpAccGeneralBody" class="border-t border-slate-100 px-3.5 py-3">${general}</div>
        </div>

        <!-- Palm Inventory (survey counts ± manual tree edits; re-rendered by refreshInventory) -->
        <div id="dpInventory" class="rounded-lg border border-slate-200 overflow-hidden">${buildInventoryHTML(feature)}</div>
      </div>`;
}

// Shared accordion header for the panel cards. `badge` is the muted value on the right.
function accHead(bodyId, icon, title, badge, open) {
    return `
      <button type="button" data-acc="${bodyId}" class="dp-acc-head w-full flex items-center gap-2.5 px-3.5 py-3 bg-white hover:bg-slate-50 text-left">
        ${icon}
        <span class="flex-1 text-sm font-semibold text-slate-900">${title}</span>
        <span class="font-mono text-[11px] text-slate-500">${badge || ''}</span>
        <i class="dp-acc-chev bi bi-chevron-${open ? 'up' : 'down'} text-slate-400 text-xs"></i>
      </button>`;
}

// Per-category tree counts, best source first:
//   1. the live working set, while the editor has this block's trees loaded (instant feedback);
//   2. `tree_counts` from the database (import_trees has been run);
//   3. the drone-survey stats in `properties` (before the trees were imported).
function effectiveTreeCounts(feature) {
    const names = Object.keys(PALM_CONFIG);
    const counts = { __total: 0, __removed: 0 };
    names.forEach(n => { counts[n] = 0; });

    if (treeBase) {
        treeList().forEach(t => {
            if (t.status === 'deleted') return;
            if (t.status === 'removed') { counts.__removed++; return; }
            if (counts[t.category] === undefined) counts[t.category] = 0;
            counts[t.category]++;
        });
    } else if (feature.tree_counts && feature.tree_counts.__total) {
        names.forEach(n => { counts[n] = feature.tree_counts[n] || 0; });
        counts.__removed = feature.tree_counts.__removed || 0;
    } else {
        const p = blockProps(feature);
        names.forEach(n => { counts[n] = p[PALM_CONFIG[n].prop] || 0; });
    }
    counts.__total = names.reduce((a, n) => a + (counts[n] || 0), 0);
    return counts;
}

function buildInventoryHTML(feature) {
    const p = blockProps(feature);
    const c = effectiveTreeCounts(feature);
    const rows = `
            ${palmRow('palm_normal', p, c.__total, c)}
            ${palmRow('palm_abnormal', p, c.__total, c)}
            ${palmRow('palm_tothin', p, c.__total, c)}
            ${palmRow('palm_overplanted', p, c.__total, c)}
            ${palmRow('palm_coconut', p, c.__total, c)}
            ${c.__removed ? statRow('Pruned / Removed', fmtInt(c.__removed), 'text-slate-500') : ''}
            ${statRow('Vacant Points', fmtInt(p['#VacantPoint']))}`;

    if (!editing) {
        return `
          <h4 class="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 border-b border-slate-100 pb-1">
            Palm Inventory (${fmtInt(c.__total)} total)
          </h4>${rows}`;
    }
    return `
          ${accHead('dpAccPalmBody', '<span class="w-[17px] text-center text-base leading-none">🌴</span>', 'Palm Inventory', `${fmtInt(c.__total)} total`, true)}
          <div id="dpAccPalmBody" class="border-t border-slate-100 px-3.5 py-3">${rows}</div>`;
}

// Re-render just the inventory block (after a tree add / remove / delete / undo / save).
function refreshInventory() {
    const wrap = document.getElementById('dpInventory');
    if (!wrap || !secBlockFeature) return;
    wrap.innerHTML = buildInventoryHTML(secBlockFeature);
    wirePalmToggles(secBlockFeature);
    refreshPalmLayers();
}

function wirePalmToggles() {
    document.querySelectorAll('#dpContent .palm-toggle-btn').forEach(btn => {
        btn.classList.toggle('on', !!activePalmLayers[btn.dataset.palm]);   // keep the switch in sync with the map
        btn.addEventListener('click', () => togglePalm(btn.dataset.palm));
    });
}

// ── Sub-blocks (task areas) — working-set editor with undo + explicit save ──
let secBlockId = null;          // parent block id
let secBlockFeature = null;     // parent block feature (for code/lat on the server)
let workFC = null;              // working FeatureCollection (unsaved edits live here)
let undoStack = [];             // snapshots of workFC.features (JSON)
let dirty = false;              // unsaved changes?
const secSelected = new Set();  // selected working ids (__wid)
const secLayersById = {};       // __wid -> leaflet layer
const secOutlines = {};         // __wid -> selection outline (blue line drawn ABOVE the roads)
let widSeq = 1;
let eraseMode = false, erasing = false, brushM = 8, brushCursor = null, brushPath = [];
let cutMode = false, cutting = false, cutPath = [], cutLine = null;
let penMode = false, penVerts = [], penRubber = null;   // pen tool: click-to-place anchor points
let spacePan = false;                                   // hold SPACE to pan while a draw tool is active
let sectionsVisible = true;     // show/hide the saved sub-block layer
let editing = false;            // full-screen sub-block editor open? (same map, pinned over the viewport)
let splitSetupOpen = false;     // road picker + Preview / Split sub-blocks actions
let resplitSnap = null;         // sub-blocks as they were when Re-split opened (Cancel restores them)

function clearSectionLayer() {
    if (sectionLayer) { blockmap.removeLayer(sectionLayer); sectionLayer = null; }
    clearSelectionOutlines();
    Object.keys(secLayersById).forEach(k => delete secLayersById[k]);
    secSelected.clear();
    endErase(); endCut(); endPen();
}

function resetSectionState() {
    clearSectionLayer();
    resetTreeState();
    workFC = null; undoStack = []; dirty = false; widSeq = 1;
}

function secStyle(wid, color) {
    const on = secSelected.has(wid);
    return { color: on ? '#2563eb' : '#ffffff', weight: on ? 3.5 : 1.5,
             fillColor: color, fillOpacity: on ? 0.6 : 0.45, opacity: 0.95 };
}

// Install a features array as the working set (assign wid + colour), render it.
function setWork(features, { saved }) {
    clearSectionLayer();
    workFC = { type: 'FeatureCollection', features: (features || []).map((f, i) => {
        const props = Object.assign({}, f.properties || {});
        return { type: 'Feature', __wid: widSeq++, __color: SECTION_PALETTE[i % SECTION_PALETTE.length],
                 properties: props, geometry: f.geometry, id: f.id };
    }) };
    dirty = !saved;
    sectionsVisible = true;   // freshly generated/loaded → show them
    const hasFeats = workFC.features.length > 0;
    document.getElementById('dpSectionsToggleRow').classList.toggle('hidden', !hasFeats);
    document.getElementById('dpSectionsToggle').classList.toggle('on', sectionsVisible);
    renderWork();
    syncModeUI();
}

// Show or hide the saved sub-block layer (toggle). Off = just the parent block.
function applySectionsVisibility() {
    if (!workFC || !workFC.features.length) return;
    const toggle = document.getElementById('dpSectionsToggle');
    if (sectionsVisible) {
        toggle.classList.add('on');
        renderWork();
    } else {
        toggle.classList.remove('on');
        clearSectionLayer();   // hide the layer; workFC stays in memory
    }
    syncModeUI();              // edit tools only make sense while the sub-blocks are visible
}

function renderWork() {
    clearSectionLayer();
    if (!workFC || !workFC.features.length) { updateSectionUI(); return; }
    sectionLayer = L.geoJSON(workFC, {
        style: (feat) => secStyle(feat.__wid, feat.__color),
        onEachFeature: (feat, layer) => {
            const pr = feat.properties || {};
            const palms = pr.palm_count != null ? ` · 🌴 ${pr.palm_count}` : '';
            layer.bindTooltip(`${pr.name || ''}<br>${(pr.area_ha != null ? pr.area_ha + ' ha' : '')}${palms}`,
                { permanent: true, direction: 'center', className: 'leaflet-tooltip' });
            secLayersById[feat.__wid] = layer;
            layer.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                if (penMode) { addPenPoint(e.latlng); return; }   // drop an anchor even when clicking on a polygon
                if (treeMode) { addTree(e.latlng); return; }      // tree management: add a tree on top of a sub-block
                if (eraseMode || cutMode) return;
                pickSubblock(feat.__wid);
            });
        }
    }).addTo(blockmap);
    updateSectionUI();
}

function toggleSecSelect(wid) {
    if (secSelected.has(wid)) secSelected.delete(wid); else secSelected.add(wid);
    const f = workFC.features.find(x => x.__wid === wid);
    const lyr = secLayersById[wid];
    if (lyr && f) lyr.setStyle(secStyle(wid, f.__color));
    setSelectionOutline(wid, secSelected.has(wid));   // blue outline only, above the roads
    updateSectionUI();
}

// A sub-block click (map or list). The editor multi-selects (Merge needs 2+); normal mode
// picks one sub-block at a time and moves the map to it. Clicking the picked one again
// clears it. Returns whether `wid` ends up selected.
function pickSubblock(wid) {
    if (editing) { toggleSecSelect(wid); return secSelected.has(wid); }
    const wasOnly = secSelected.size === 1 && secSelected.has(wid);
    [...secSelected].forEach(w => { if (w !== wid) toggleSecSelect(w); });
    if (wasOnly) { toggleSecSelect(wid); return false; }
    if (!secSelected.has(wid)) toggleSecSelect(wid);
    focusSubblock(wid);
    return true;
}

// One undo stack for BOTH editors: each snapshot holds the sub-block working set and the
// pending tree edits, so Undo always steps back exactly one action whichever tool made it.
function pushUndo(sections = true) {
    undoStack.push(JSON.stringify({ s: workFC ? workFC.features : null, t: treeEdits, r: rows, d: rowDraft }));
    if (undoStack.length > 30) undoStack.shift();
    if (sections) dirty = true;
}

function doUndo() {
    if (!undoStack.length) return;
    const prev = JSON.parse(undoStack.pop());
    if (workFC && prev.s) workFC.features = prev.s;
    treeEdits = prev.t || {};
    rows = prev.r || []; rowDraft = prev.d || [];
    treeSelected = null;
    secSelected.clear();
    renderWork();
    renderRows();
    renderTrees();
    refreshInventory();
}

function selectedFeatures() { return workFC.features.filter(f => secSelected.has(f.__wid)); }

function updateSectionUI() {
    const total = workFC ? workFC.features.reduce((a, f) => a + (f.properties.palm_count || 0), 0) : 0;
    const n = workFC ? workFC.features.length : 0;
    const info = document.getElementById('dpSectionInfo');
    // While a draw tool is active, keep its hint text (don't overwrite with the summary).
    if (info && !cutMode && !eraseMode && !penMode) info.innerHTML = n ? `${n} sub-blocks · 🌴 ${total} trees` + (dirty ? ' <span style="color:#f59e0b">(unsaved)</span>' : ' <span style="color:#22c55e">(saved)</span>') : 'No sub-blocks yet.';
    const mergeBtn = document.getElementById('dpMergeBtn');
    if (mergeBtn) {
        const one = secSelected.size === 1;
        mergeBtn.disabled = secSelected.size < 2 || eraseMode || cutMode || penMode || treeMode || rowMode;
        document.getElementById('dpCutBtn').disabled = !one || eraseMode || penMode || treeMode || rowMode;
        document.getElementById('dpEraseBtn').disabled = !one || cutMode || penMode || treeMode || rowMode;
        const delBtn = document.getElementById('dpDeleteSubBtn');
        if (delBtn) delBtn.disabled = secSelected.size < 1 || eraseMode || cutMode || penMode || treeMode || rowMode;
        const undoBtn = document.getElementById('dpUndoBtn');
        // In pen mode the Undo button steps back one point; otherwise it pops the edit history.
        undoBtn.disabled = penMode ? (cutPath.length === 0) : (undoStack.length === 0);
        undoBtn.innerHTML = penMode
            ? '<i class="bi bi-arrow-counterclockwise"></i> Undo point'
            : '<i class="bi bi-arrow-counterclockwise"></i> Undo';
        document.getElementById('dpCutBtn').classList.toggle('!bg-blue-600', cutMode);
        document.getElementById('dpCutBtn').classList.toggle('!text-white', cutMode);
        document.getElementById('dpEraseBtn').classList.toggle('!bg-emerald-600', eraseMode);
        document.getElementById('dpEraseBtn').classList.toggle('!text-white', eraseMode);
        const penBtn = document.getElementById('dpPenBtn');
        if (penBtn) {
            penBtn.disabled = !one || cutMode || eraseMode || treeMode || rowMode;
            penBtn.classList.toggle('!bg-blue-600', penMode);
            penBtn.classList.toggle('!text-white', penMode);
        }
    }
    updateUnsavedUI();   // owns the Save button's label AND its disabled state
    updateCutApply();
    renderSubblockList();
    markSubblockRows();
    updateTreeUI();
    updateRowUI();
    // While any draw tool (or tree placement / row mapping) is active, roads ignore clicks (CSS).
    if (blockmap) blockmap.getContainer().classList.toggle('nh-drawing', cutMode || penMode || eraseMode || treeMode || rowMode);
}

// ── Sub-blocks accordion (detail panel): list each task area with tree count + ha, inline rename ──
let _sbSig = null;   // signature of the rendered list — skip rebuild while unchanged (don't clobber an open edit)
// One sub-block card. `editable` adds the edit controls (one pencil for the name, one for the
// area in ha) and the "unsaved" tag (editor); the /block/all view card is read-only.
function subblockRowHTML(f, i, editable) {
    const pr = f.properties || {};
    const nm = pr.name || '';
    const idx = String(pr.section_index || (i + 1)).padStart(2, '0');
    const trees = pr.palm_count != null ? fmtInt(pr.palm_count) : '—';
    const ha = pr.area_ha != null ? fmt(pr.area_ha, 2) : '—';
    const nmAttr = nm.replace(/"/g, '&quot;');
    // Editing is only offered for persisted sub-blocks; unsaved (just-previewed) ones get
    // their T-NN names and areas assigned on Save, so an edit there wouldn't stick.
    const unsaved = editable && !f.id ? ' <span class="text-[10px] font-medium text-amber-500">unsaved</span>' : '';
    const manual = pr.area_manual ? ' <span class="text-[10px] font-medium text-slate-400" title="Area set by hand">edited</span>' : '';
    const renameBtn = editable && f.id ? `<button type="button" class="sb-edit-btn text-slate-300 hover:text-blue-600 text-sm leading-none shrink-0" data-field="name" title="Rename"><i class="bi bi-pencil"></i></button>` : '';
    const areaBtn = editable && f.id ? ` <button type="button" class="sb-edit-btn text-slate-300 hover:text-blue-600 leading-none" data-field="area_ha" title="Edit area"><i class="bi bi-pencil"></i></button>` : '';
    const editRow = editable ? `
            <div class="sb-edit hidden mt-2 items-center gap-1.5" data-field="name">
              <input class="sb-input flex-1 min-w-0 border border-slate-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" value="${nmAttr}">
              <button type="button" class="sb-save inline-flex items-center justify-center h-7 w-7 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-sm leading-none shrink-0" title="Save"><i class="bi bi-check-lg"></i></button>
              <button type="button" class="sb-cancel inline-flex items-center justify-center h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm leading-none shrink-0" title="Cancel"><i class="bi bi-x-lg"></i></button>
            </div>
            <div class="sb-edit hidden mt-2 items-center gap-1.5" data-field="area_ha">
              <input type="number" step="0.01" min="0.01" class="sb-input flex-1 min-w-0 border border-slate-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" value="${pr.area_ha != null ? Number(pr.area_ha).toFixed(2) : ''}">
              <span class="text-xs text-slate-500">ha</span>
              <button type="button" class="sb-save inline-flex items-center justify-center h-7 w-7 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-sm leading-none shrink-0" title="Save"><i class="bi bi-check-lg"></i></button>
              <button type="button" class="sb-cancel inline-flex items-center justify-center h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm leading-none shrink-0" title="Cancel"><i class="bi bi-x-lg"></i></button>
            </div>` : '';
    return `<div class="sb-row group cursor-pointer rounded-lg border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-sm transition-colors p-2.5" data-id="${f.id || ''}" data-wid="${f.__wid}">
            <div class="flex items-center gap-2">
              <span class="h-6 w-6 rounded-md flex items-center justify-center text-[11px] font-bold text-white shrink-0" style="background:${f.__color || '#94a3b8'}">${idx}</span>
              <span class="sb-name text-sm font-semibold text-slate-800 truncate flex-1 min-w-0">${nm}</span>${unsaved}
              ${renameBtn}
            </div>
            <div class="mt-1.5 pl-8 flex items-center gap-2 text-xs text-slate-500">
              <span class="inline-flex items-center gap-1">🌴 <b class="text-slate-700">${trees}</b> trees</span>
              <span class="text-slate-300">·</span>
              <span class="inline-flex items-center gap-1"><i class="bi bi-bounding-box-circles text-slate-400"></i> <b class="text-slate-700">${ha}</b> ha${manual}${areaBtn}</span>
            </div>${editRow}
          </div>`;
}

// Read-only "Sub-blocks" card in the /block/all panel. Every lookup is null-safe: if the
// page HTML is older than this script (template cache), the card is just skipped instead
// of throwing and stopping the panel from opening.
function renderViewSubblocks(feats) {
    const card = document.getElementById('dpViewSub');
    const list = document.getElementById('dpViewSubList');
    if (!card || !list) return;
    if (!feats || !feats.length) { card.classList.add('hidden'); return; }
    card.classList.remove('hidden');
    const badge = document.getElementById('dpViewSubBadge');
    if (badge) badge.textContent = feats.length;
    list.innerHTML = feats.map((f, i) => subblockRowHTML(f, i, false)).join('');
}
function hideViewSubblocks() {
    const card = document.getElementById('dpViewSub');
    if (card) card.classList.add('hidden');
    closeAcc('dpViewSubBody');
}

function renderSubblockList() {
    const wrap = document.getElementById('dpSubblocks');
    if (!wrap) return;
    const feats = (workFC && workFC.features) || [];
    if (!feats.length) { wrap.classList.add('hidden'); hideViewSubblocks(); _sbSig = null; return; }
    // Rebuild only when the set actually changes (count / name / ha / trees / colour).
    const sig = feats.map(f => `${f.id || ''}|${f.properties.name}|${f.properties.area_ha}|${f.properties.area_manual}|${f.properties.palm_count}|${f.__color}`).join(';');
    if (sig === _sbSig) return;
    _sbSig = sig;
    wrap.classList.remove('hidden');
    document.getElementById('dpSubblocksCount').textContent = feats.length;
    document.getElementById('dpSubblocksList').innerHTML = feats.map((f, i) => subblockRowHTML(f, i, true)).join('');
    renderViewSubblocks(feats);
}

// Ring the accordion rows whose sub-block is currently selected (keeps the list in sync with
// the map's blue selection, both ways). Cheap — restyles existing rows, never rebuilds.
function markSubblockRows() {
    document.querySelectorAll('#dpSubblocksList .sb-row, #dpViewSubList .sb-row').forEach(row => {
        const on = secSelected.has(parseInt(row.getAttribute('data-wid'), 10));
        row.classList.toggle('ring-2', on);
        row.classList.toggle('ring-blue-500', on);
        row.classList.toggle('border-blue-400', on);
    });
}

// Pan the map to a sub-block so the highlight is visible when picked from the list. Keeps the
// current zoom (only pans / zooms out to fit) and offsets for the detail panel on the right.
function focusSubblock(wid) {
    const lyr = secLayersById[wid];
    if (!lyr || !blockmap || !lyr.getBounds) return;
    const bounds = lyr.getBounds();
    if (!bounds || !bounds.isValid()) return;
    const panel = document.getElementById('blockDetailPanel');
    const mapW = blockmap.getSize().x;
    const panelW = (panel && !panel.classList.contains('translate-x-full')) ? panel.offsetWidth : 0;
    if (panelW && panelW < mapW * 0.6) {
        blockmap.fitBounds(bounds, { paddingTopLeft: [20, 20], paddingBottomRight: [panelW + 20, 20], maxZoom: blockmap.getZoom() });
    } else {
        blockmap.panTo(bounds.getCenter());
    }
}

// Apply a name / area edit to the working set + map tooltip (and force the accordion to rebuild).
function applyEditLocal(wid, props) {
    const f = workFC && workFC.features.find(x => x.__wid === wid);
    if (f) Object.assign(f.properties, props);
    _sbSig = null;
    renderWork();   // refreshes the on-map label + re-runs updateSectionUI → renderSubblockList
}

// Save one sub-block edit form — the name, or the area in ha (each has its own pencil).
function saveSubblockEdit(form) {
    if (!form) return;
    const row = form.closest('.sb-row');
    const id = row.getAttribute('data-id');
    const wid = parseInt(row.getAttribute('data-wid'), 10);
    const field = form.getAttribute('data-field');
    const raw = (form.querySelector('.sb-input').value || '').trim();
    const data = { sub_block_id: id, block_id: secBlockId };
    let local;
    if (field === 'name') {
        if (!raw) { Toast.show('Sub-block name is required.', 'error'); return; }
        data.name = raw;
        local = { name: raw };
    } else {
        const areaHa = Math.round(parseFloat(raw) * 100) / 100;
        if (!(areaHa > 0)) { Toast.show('Area must be greater than 0 ha.', 'error'); return; }
        data.area_ha = areaHa;
        local = { area_ha: areaHa, area_manual: true };
    }
    // Not yet persisted (freshly split, no DB id) → update the working set; the panel's Save persists it.
    if (!id) { applyEditLocal(wid, local); Toast.show('Updated — click Save to persist.', 'info'); return; }
    const saveBtn = form.querySelector('.sb-save'); saveBtn.disabled = true;
    $.ajax({
        type: 'POST', url: '/api/block/section/update',
        data: data,
        headers: { 'X-CSRFToken': csrf() },
        success: function (res) {
            const sb = res.sub_block || {};
            applyEditLocal(wid, { name: sb.name, area_ha: sb.area_ha, area_manual: !!sb.area_manual });
            Toast.show(field === 'name' ? 'Sub-block renamed.' : 'Sub-block area updated.', 'success');
        },
        error: function (xhr) { Toast.show('Error: ' + (xhr.responseJSON?.error || xhr.responseText), 'error'); saveBtn.disabled = false; }
    });
}

// Save one General-card edit (block name, Gross Area or Planted Area).
function saveBlockField(wrap) {
    if (!wrap || !secBlockFeature) return;
    const field = wrap.getAttribute('data-field');
    const raw = (wrap.querySelector('.dp-edit-input').value || '').trim();
    const data = {};
    if (field === 'name') {
        if (!raw) { Toast.show('Block name is required.', 'error'); return; }
        data.name = raw;
    } else {
        data[field] = Math.round(parseFloat(raw) * 100) / 100;
        if (!(data[field] > 0)) { Toast.show('Area must be greater than 0 ha.', 'error'); return; }
    }
    const msg = { name: 'Block renamed.', area_ha: 'Block area updated.', planted_ha: 'Planted area updated.' }[field];
    updateBlock(data, msg, wrap.querySelector('.dp-edit-save'));
}

// Send one change to /api/block/update, then refresh every place the block's values show.
function updateBlock(data, okMsg, btn) {
    const feature = secBlockFeature;
    if (btn) btn.disabled = true;
    $.ajax({
        type: 'POST', url: '/api/block/update',
        data: Object.assign({ block_id: feature.id }, data),
        headers: { 'X-CSRFToken': csrf() },
        success: function (res) { applyBlockUpdate(feature, res.block || {}); Toast.show(okMsg, 'success'); },
        error: function (xhr) { Toast.show('Error: ' + (xhr.responseJSON?.error || xhr.responseText), 'error'); },
        complete: function () { if (btn) btn.disabled = false; }
    });
}

// Copy the saved values onto the block feature and redraw its labels, dropdown row,
// the Total Area card and the panel (Planted follows Gross unless it was set by hand).
function applyBlockUpdate(feature, saved) {
    Object.assign(feature, saved);
    const label = blockLabel(feature);
    $('#dpTitle').text(label);
    $('#blockDropdownLabel').text(label);
    $('#selectedBlockLabel').text(label);
    if (layerByBlockId[feature.id]) layerByBlockId[feature.id].setTooltipContent(label);
    const $row = $(`.block-row[data-block-id="${feature.id}"]`);
    $row.find('.block-row-name').text(feature.name);
    $row.attr('data-search', `${feature.name} ${feature.code || ''}`.toLowerCase());
    $row.find('.block-row-area').text(`${fmt(feature.area_ha, 2)} Ha · ${fmt(feature.area_acre, 2)} Acre`);
    $row.find('.block-row-planted').text(`${fmt(feature.planted_area, 2)} Ha planted`);
    const feats = blocks.features || [];
    $('#blocksTotalHa').text(`${fmt(feats.reduce((t, f) => t + (+f.area_ha || 0), 0), 2)} Ha`);
    $('#blocksTotalPlanted').text(`${fmt(feats.reduce((t, f) => t + (+f.planted_area || 0), 0), 2)} Ha`);
    $('#blocksTotalAcre').text(`${fmt(feats.reduce((t, f) => t + (+f.area_acre || 0), 0), 2)} Acre`);
    document.getElementById('dpContent').innerHTML = buildDetailHTML(feature);
    wirePalmToggles();
}

$(document).on('click', '.dp-acc-head', function () {
    const body = document.getElementById(this.getAttribute('data-acc'));
    if (!body) return;
    const open = !body.classList.toggle('hidden');
    const chev = this.querySelector('.dp-acc-chev');
    if (chev) { chev.classList.toggle('bi-chevron-up', open); chev.classList.toggle('bi-chevron-down', !open); }
});

// Fold one accordion card shut (used when the editor opens or another block is selected).
function closeAcc(bodyId) {
    const body = document.getElementById(bodyId);
    if (!body) return;
    body.classList.add('hidden');
    const head = document.querySelector(`.dp-acc-head[data-acc="${bodyId}"] .dp-acc-chev`);
    if (head) { head.classList.remove('bi-chevron-up'); head.classList.add('bi-chevron-down'); }
}

// Accordion header collapse/expand + inline edit (name / area) interactions (delegated; attach once).
$(document).on('click', '#dpSubblocksHead', function () {
    const list = document.getElementById('dpSubblocksList');
    const chev = document.getElementById('dpSubblocksChev');
    const collapsed = list.classList.toggle('hidden');
    chev.classList.toggle('bi-chevron-down', !collapsed);
    chev.classList.toggle('bi-chevron-right', collapsed);
});
$(document).on('click', '#dpSubblocksList .sb-row', function (e) {
    // Edit controls have their own actions — don't treat those clicks as a row select.
    if (e.target.closest('.sb-edit-btn') || e.target.closest('.sb-edit')) return;
    const wid = parseInt(this.getAttribute('data-wid'), 10);
    toggleSecSelect(wid);                              // blue outline + restyle, same as clicking on the map
    if (secSelected.has(wid)) focusSubblock(wid);      // pan to it when selecting (not when deselecting)
});
// View card row: pick just this sub-block and move the map to it (turning the sub-block
// layer on first if it was switched off).
$(document).on('click', '#dpViewSubList .sb-row', function () {
    const wid = parseInt(this.getAttribute('data-wid'), 10);
    if (!sectionsVisible) { sectionsVisible = true; applySectionsVisibility(); }
    pickSubblock(wid);
});
// Each pencil opens its own form (name or area); opening one closes the other.
$(document).on('click', '#dpSubblocksList .sb-edit-btn', function () {
    const row = this.closest('.sb-row');
    row.querySelectorAll('.sb-edit').forEach(ed => { ed.classList.add('hidden'); ed.classList.remove('flex'); });
    const ed = row.querySelector(`.sb-edit[data-field="${this.getAttribute('data-field')}"]`);
    ed.classList.remove('hidden'); ed.classList.add('flex');
    const inp = ed.querySelector('.sb-input'); inp.focus(); inp.select();
});
$(document).on('click', '#dpSubblocksList .sb-cancel', function () {
    const ed = this.closest('.sb-edit');
    ed.classList.add('hidden'); ed.classList.remove('flex');
    const inp = ed.querySelector('.sb-input'); inp.value = inp.defaultValue;
});
$(document).on('click', '#dpSubblocksList .sb-save', function () { saveSubblockEdit(this.closest('.sb-edit')); });
$(document).on('keydown', '#dpSubblocksList .sb-input', function (e) {
    if (e.key === 'Enter') { e.preventDefault(); saveSubblockEdit(this.closest('.sb-edit')); }
    if (e.key === 'Escape') { this.closest('.sb-edit').querySelector('.sb-cancel').click(); }
});

// Block name / Gross Area / Planted Area inline edit (General card, editor only) — one pencil each.
$(document).on('click', '#dpContent .dp-edit-btn', function () {
    const form = this.closest('.dp-edit').querySelector('.dp-edit-form');
    form.classList.remove('hidden'); form.classList.add('flex');
    const inp = form.querySelector('.dp-edit-input'); inp.focus(); inp.select();
});
$(document).on('click', '#dpContent .dp-edit-cancel', function () {
    const form = this.closest('.dp-edit-form');
    form.classList.add('hidden'); form.classList.remove('flex');
    const inp = form.querySelector('.dp-edit-input'); inp.value = inp.defaultValue;
});
$(document).on('click', '#dpContent .dp-edit-save', function () { saveBlockField(this.closest('.dp-edit')); });
$(document).on('click', '#dpContent .dp-planted-reset', function () {
    updateBlock({ reset_planted: 1 }, 'Planted area set back to the formula.', this);
});
$(document).on('keydown', '#dpContent .dp-edit-input', function (e) {
    if (e.key === 'Enter') { e.preventDefault(); saveBlockField(this.closest('.dp-edit')); }
    if (e.key === 'Escape') { this.closest('.dp-edit').querySelector('.dp-edit-cancel').click(); }
});

function loadSavedSections(blockId) {
    resetSectionState();
    secBlockId = blockId;
    $.get('/api/block/section/list', { block_id: blockId }).done(function (fc) {
        if (fc && fc.features && fc.features.length) {
            setWork(fc.features, { saved: true });
            document.getElementById('dpSplitLabel').textContent = `Re-split (${fc.features.length} saved)`;
        } else {
            document.getElementById('dpSectionInfo').textContent = 'No sub-blocks yet.';
            document.getElementById('dpSplitLabel').textContent = 'Split into sub-blocks';
            document.getElementById('dpSectionsToggleRow').classList.add('hidden');
            document.getElementById('dpSubblocks').classList.add('hidden');   // no accordion when not split
            hideViewSubblocks();
            _sbSig = null;
            syncModeUI();
        }
    });
}

// ── Edit ops (compute on server, apply to working set, NO auto-save) ──
function csrf() { return $('[name=csrfmiddlewaretoken]').val(); }

function doMerge() {
    const picked = selectedFeatures();
    if (picked.length < 2) return;
    const lb = new LoadingButton($('#dpMergeBtn')); lb.start();
    $.ajax({
        type: 'POST', url: '/api/block/section/merge',
        data: { block_id: secBlockId, geometries: JSON.stringify(picked.map(f => f.geometry)) },
        headers: { 'X-CSRFToken': csrf() },
        success: function (res) {
            pushUndo();
            const ids = new Set(picked.map(f => f.__wid));
            workFC.features = workFC.features.filter(f => !ids.has(f.__wid));
            workFC.features.push({ type: 'Feature', __wid: widSeq++, __color: picked[0].__color,
                properties: res.feature.properties, geometry: res.feature.geometry });
            secSelected.clear();
            renderWork();
            Toast.show('Merged.', 'success');
        },
        error: function (xhr) { Toast.show('Merge: ' + (xhr.responseJSON?.error || 'failed'), 'error'); },
        complete: function () { lb.stop(); }
    });
}

// Delete the selected sub-block(s) from the working set (Undo restores; Save persists).
function doDeleteSub() {
    if (!secSelected.size) return;
    pushUndo();
    const n = secSelected.size;
    workFC.features = workFC.features.filter(f => !secSelected.has(f.__wid));
    secSelected.clear();
    renderWork();
    Toast.show(n > 1 ? `Deleted ${n} sub-blocks. Save to apply.` : 'Sub-block deleted. Save to apply.', 'success');
}

function applyParts(targetWid, parts, msg) {
    pushUndo();
    const tgt = workFC.features.find(f => f.__wid === targetWid);
    const color = tgt ? tgt.__color : SECTION_PALETTE[0];
    workFC.features = workFC.features.filter(f => f.__wid !== targetWid);
    parts.forEach((p, i) => workFC.features.push({ type: 'Feature', __wid: widSeq++,
        __color: i === 0 ? color : SECTION_PALETTE[(workFC.features.length + i) % SECTION_PALETTE.length],
        properties: p.properties, geometry: p.geometry }));
    secSelected.clear();
    renderWork();
    Toast.show(msg, 'success');
}

// ── Cut (Photoshop-style: draw the mask line, then click "Apply cut") ──
function startCut() {
    if (secSelected.size !== 1) return;
    cutMode = true; cutting = false; cutPath = [];
    if (cutLine) { blockmap.removeLayer(cutLine); cutLine = null; }
    blockmap.dragging.disable(); blockmap._container.style.cursor = 'crosshair';
    const row = document.getElementById('dpCutRow'); if (row) row.classList.remove('hidden');
    updateSectionUI();
    setCutHint('✏️ Draw a line across the sub-block, then click “Apply cut”.');
}
function endCut() {
    cutMode = false; cutting = false; cutPath = [];
    if (blockmap) { blockmap.dragging.enable(); blockmap._container.style.cursor = ''; }
    if (cutLine) { blockmap.removeLayer(cutLine); cutLine = null; }
    const row = document.getElementById('dpCutRow'); if (row) row.classList.add('hidden');
    updateSectionUI();
}
// True once a usable mask line exists (freehand released, or 2+ pen points) and is waiting to be applied.
function cutHasMask() { return ((cutMode && !cutting) || penMode) && cutPath.length >= 2; }
function endActiveCutTool() { if (penMode) endPen(); else endCut(); }
function updateCutApply() {
    const btn = document.getElementById('dpCutApplyBtn');
    if (btn) btn.disabled = !cutHasMask();
}
function setCutHint(text) {
    const info = document.getElementById('dpSectionInfo');
    if (info) info.textContent = text;
}
// On mouse-up: keep the drawn line as a PENDING mask — do not cut yet.
function finalizeCutStroke() {
    if (cutPath.length < 2) {
        if (cutLine) { blockmap.removeLayer(cutLine); cutLine = null; }
        cutPath = [];
        updateCutApply();
        setCutHint('✏️ Draw a line across the sub-block, then click “Apply cut”.');
        return;
    }
    updateCutApply();
    setCutHint('✂️ Line ready — click “Apply cut”, or draw again to redo.');
}
// Explicit "Apply cut": split the selected sub-block along the drawn mask (freehand or pen).
function commitCut() {
    if (!cutHasMask()) return;
    const wid = [...secSelected][0]; const f = workFC.features.find(x => x.__wid === wid);
    if (!f) { endActiveCutTool(); return; }
    const path = cutPath.slice();
    const lb = new LoadingButton($('#dpCutApplyBtn')); lb.start();
    $.ajax({
        type: 'POST', url: '/api/block/section/cut',
        data: { block_id: secBlockId, geometry: JSON.stringify(f.geometry), path: JSON.stringify(path) },
        headers: { 'X-CSRFToken': csrf() },
        success: function (res) { endActiveCutTool(); applyParts(wid, res.parts, `Split into ${res.parts.length} sub-blocks.`); },
        error: function (xhr) { endActiveCutTool(); Toast.show((xhr.responseJSON?.error || 'Cut failed'), 'error'); },
        complete: function () { lb.stop(); }
    });
}

// ── Pen tool (click point-by-point, like Photoshop's pen) ──
function startPen() {
    if (secSelected.size !== 1) return;
    endCut(); endErase();
    penMode = true; cutPath = [];
    if (cutLine) { blockmap.removeLayer(cutLine); cutLine = null; }
    penVerts.forEach(m => blockmap.removeLayer(m)); penVerts = [];
    if (penRubber) { blockmap.removeLayer(penRubber); penRubber = null; }
    blockmap.doubleClickZoom.disable();                  // pan stays enabled; clicks place points
    blockmap._container.style.cursor = 'crosshair';
    const row = document.getElementById('dpCutRow'); if (row) row.classList.remove('hidden');
    updateSectionUI();
    setCutHint('🖊️ Click to place points, then “Apply cut”. Right-click removes the last point.');
}
function endPen() {
    penMode = false; cutPath = [];
    if (blockmap) { blockmap.doubleClickZoom.enable(); blockmap._container.style.cursor = ''; }
    if (cutLine) { blockmap.removeLayer(cutLine); cutLine = null; }
    penVerts.forEach(m => blockmap.removeLayer(m)); penVerts = [];
    if (penRubber) { blockmap.removeLayer(penRubber); penRubber = null; }
    const row = document.getElementById('dpCutRow'); if (row) row.classList.add('hidden');
    updateSectionUI();
}
function addPenPoint(latlng) {
    if (!penMode || spacePan) return;
    cutPath.push([latlng.lng, latlng.lat]);
    redrawPen();
    setCutHint(cutPath.length < 2 ? '🖊️ Click to add the next point…'
                                  : '🖊️ Keep clicking, then “Apply cut”. Undo (or right-click) removes the last point.');
    updateSectionUI();   // refresh Apply + Undo button states
}
// Step back one pen point (Undo button, right-click, or Backspace while drawing).
function penUndo() {
    if (!penMode || !cutPath.length) return;
    cutPath.pop();
    redrawPen();
    if (penRubber && !cutPath.length) { blockmap.removeLayer(penRubber); penRubber = null; }
    setCutHint(cutPath.length ? '🖊️ Keep clicking, then “Apply cut”. Undo (or right-click) removes the last point.'
                              : '🖊️ Click to place points, then “Apply cut”.');
    updateSectionUI();
}
function redrawPen() {
    const latlngs = cutPath.map(p => [p[1], p[0]]);
    if (cutPath.length >= 2) {
        if (!cutLine) cutLine = L.polyline(latlngs, { color: '#2563eb', weight: 3 }).addTo(blockmap);
        else cutLine.setLatLngs(latlngs);
    } else if (cutLine) { blockmap.removeLayer(cutLine); cutLine = null; }
    penVerts.forEach(m => blockmap.removeLayer(m)); penVerts = [];
    cutPath.forEach(p => penVerts.push(
        L.circleMarker([p[1], p[0]], { radius: 4, color: '#2563eb', weight: 2, fillColor: '#fff', fillOpacity: 1 }).addTo(blockmap)
    ));
}

// ── Erase (brush; removes area) ──
function startErase() {
    if (secSelected.size !== 1) return;
    eraseMode = true; brushPath = [];
    blockmap.dragging.disable();
    document.getElementById('dpBrushRow').classList.remove('hidden');
    document.getElementById('dpSectionInfo').textContent = '🧽 Hold & drag to erase. Drag a strip across to split.';
    updateSectionUI();
}
function endErase() {
    eraseMode = false; erasing = false; brushPath = [];
    if (blockmap) blockmap.dragging.enable();
    if (brushCursor) { blockmap.removeLayer(brushCursor); brushCursor = null; }
    const row = document.getElementById('dpBrushRow'); if (row) row.classList.add('hidden');
    updateSectionUI();
}
function commitErase() {
    const wid = [...secSelected][0]; const f = workFC.features.find(x => x.__wid === wid);
    if (!f || brushPath.length < 1) { endErase(); return; }
    const path = brushPath.slice();
    $.ajax({
        type: 'POST', url: '/api/block/section/erase',
        data: { block_id: secBlockId, geometry: JSON.stringify(f.geometry), brush_path: JSON.stringify(path), brush_m: brushM },
        headers: { 'X-CSRFToken': csrf() },
        success: function (res) { endErase(); applyParts(wid, res.parts, res.parts.length > 1 ? `Split into ${res.parts.length}.` : 'Erased.'); },
        error: function (xhr) { endErase(); Toast.show((xhr.responseJSON?.error || 'Erase failed'), 'error'); }
    });
}

// `done` is called when this finishes (success or failure) — it stops the Save button's
// spinner and re-syncs its label. See saveAll().
function saveSections(done) {
    if (!workFC || !dirty) { if (done) done(); return; }
    $.ajax({
        type: 'POST', url: '/api/block/section/save',
        data: { block_id: secBlockId, features: JSON.stringify(workFC.features.map(f => ({ geometry: f.geometry, properties: f.properties }))) },
        headers: { 'X-CSRFToken': csrf() },
        success: function () { Toast.show('Saved.', 'success'); loadSavedSections(secBlockId); },
        error: function (xhr) { Toast.show('Save: ' + (xhr.responseJSON?.error || 'failed'), 'error'); },
        complete: function () { if (done) done(); }
    });
}

// The panel's single Save button: tree edits are persisted first (they feed the sub-block palm
// counts on the server), then the mapped rows, then the sub-block working set if it changed.
function saveAll() {
    if (!treeDirty() && !rowsDirty() && !(workFC && dirty)) return;
    // LoadingButton restores the label it captured at construction ("Save (1)"), which would
    // undo the "Save" that the save handlers already wrote. Re-sync the count after it stops.
    const lb = new LoadingButton($('#dpSaveBtn'));
    const done = () => { lb.stop(); updateUnsavedUI(); };
    lb.start();
    const sections = () => saveSections(done);
    const thenRows = () => { if (rowsDirty()) saveRows(sections, done); else sections(); };
    if (treeDirty()) saveTrees(thenRows, done); else thenRows();
}

// How much the one Save button would commit: the sub-block layout counts as a single change,
// then one per pending tree edit and one per added/changed/removed row.
function rowsUnsavedCount() {
    if (!rowsDirty()) return 0;
    let saved = [];
    try { saved = JSON.parse(rowsSavedSig); } catch (e) { saved = []; }
    const cur = JSON.parse(rowSig(rows));
    const savedSet = new Set(saved.map(x => JSON.stringify(x)));
    const changed = cur.filter(x => !savedSet.has(JSON.stringify(x))).length;
    return Math.max(1, changed + Math.max(0, saved.length - cur.length));
}

function unsavedCount() {
    const n = workFC ? workFC.features.length : 0;
    return (dirty && n ? 1 : 0) + Object.keys(treeEdits).length + rowsUnsavedCount();
}

// The muted value on each Edit block card header, so the counts read without opening the card.
function setAccBadge(id, text) { const el = document.getElementById(id); if (el) el.textContent = text; }

function updateAccBadges() {
    const n = workFC ? workFC.features.length : 0;
    setAccBadge('dpSubBadge', n ? `${n} ${dirty ? 'unsaved' : 'saved'}` : 'none');
    setAccBadge('dpTreeBadge', treeBase ? fmtInt(treeList().filter(t => t.status !== 'deleted').length) : '');
    setAccBadge('dpRowBadge', rows.length ? `${rows.length} rows` : 'none');
}

function updateUnsavedUI() {
    const el = document.getElementById('dpUnsavedInfo');
    const saveBtn = document.getElementById('dpSaveBtn');
    const c = unsavedCount();
    if (el) {
        el.innerHTML = c
            ? `<span style="color:#f59e0b">${c} unsaved change${c === 1 ? '' : 's'}</span>`
            : '<span style="color:#15803d">✓ All changes saved</span>';
    }
    // Nothing pending → nothing to save. This also re-disables the button after a save,
    // since LoadingButton.stop() unconditionally re-enables whatever it was attached to.
    if (saveBtn) { saveBtn.textContent = c ? `Save (${c})` : 'Save'; saveBtn.disabled = !c; }
    updateAccBadges();
}

function wireSectionPanel(feature) {
    secBlockFeature = feature;
    const splitBtn = document.getElementById('dpSplitBtn');
    const previewBtn = document.getElementById('dpPreviewBtn');
    if (!splitBtn) return;
    const chosenRoads = () => $('.road-type:checked').map(function () { return this.value; }).get();

    // The main button opens only this workflow, like Manage trees / Map rows.
    // Re-split: Done keeps whatever Preview produced; Cancel puts back the sub-blocks the
    // block had when Re-split was opened.
    const setupDoneBtn = document.getElementById('dpSplitSetupDoneBtn');
    if (setupDoneBtn) setupDoneBtn.onclick = () => { resplitSnap = null; closeSplitWorkflow(); };
    const setupCancelBtn = document.getElementById('dpSplitSetupCancelBtn');
    if (setupCancelBtn) setupCancelBtn.onclick = cancelResplit;
    splitBtn.onclick = () => {
        if (splitSetupOpen) return;   // already open — finish with Done or Cancel
        splitSetupOpen = true;
        resplitSnap = {
            feats: workFC ? JSON.stringify(workFC.features) : null,
            dirty, visible: sectionsVisible,
            info: document.getElementById('dpSectionInfo').innerHTML,
            previewed: false,
        };
        // A draw tool can't stay armed while its buttons are swapped out for the road picker.
        endCut(); endErase(); endPen();
        syncModeUI();
    };
    previewBtn.onclick = function () {
        if (dirty && !window.confirm('Previewing again will replace the current unsaved sub-block layout. Continue?')) return;
        const lb = new LoadingButton($(previewBtn)); lb.start();
        $.ajax({
            type: 'POST', url: '/api/block/section/preview',
            data: { block_id: feature.id, 'road_types[]': chosenRoads() }, traditional: true,
            headers: { 'X-CSRFToken': csrf() },
            success: function (fc) {
                // Preview shows the proposed split; Done returns to the edit tools on that layout.
                splitSetupOpen = true;
                if (resplitSnap) resplitSnap.previewed = true;   // Cancel now has something to undo
                setWork(fc.features, { saved: false });   // working set, unsaved → Save enabled (tools shown by syncModeUI)
                let msg = `${fc.count} sub-blocks · 🌴 ${fc.assigned_palms}/${fc.total_palms} assigned`;
                if (fc.outside_block) msg += ` · ${fc.outside_block} outside`;
                document.getElementById('dpSectionInfo').innerHTML = msg + ' <span style="color:#f59e0b">(unsaved)</span>';
            },
            error: function (xhr) { Toast.show('Preview: ' + (xhr.responseJSON?.error || 'failed'), 'error'); },
            complete: function () { lb.stop(); }
        });
    };

    document.getElementById('dpMergeBtn').onclick = doMerge;
    document.getElementById('dpCutBtn').onclick = () => { if (cutMode) endCut(); else startCut(); };
    const _cutApplyBtn = document.getElementById('dpCutApplyBtn');
    if (_cutApplyBtn) _cutApplyBtn.onclick = commitCut;   // guard: a missing node must not abort the rest of the wiring
    const _penBtn = document.getElementById('dpPenBtn');
    if (_penBtn) _penBtn.onclick = () => { if (penMode) endPen(); else startPen(); };
    document.getElementById('dpEraseBtn').onclick = () => { if (eraseMode) endErase(); else startErase(); };
    const _delSubBtn = document.getElementById('dpDeleteSubBtn');
    if (_delSubBtn) _delSubBtn.onclick = doDeleteSub;
    document.getElementById('dpUndoBtn').onclick = () => { if (penMode) penUndo(); else doUndo(); };
    document.getElementById('dpSaveBtn').onclick = saveAll;
    // Tree management (edit mode): toggle + actions on the selected tree; Undo shares the section stack.
    document.getElementById('dpTreeBtn').onclick = () => { if (treeMode) exitTreeMode(); else enterTreeMode(); };
    const treeDone = document.getElementById('dpTreeDoneBtn');
    if (treeDone) treeDone.onclick = () => { if (treeMode) exitTreeMode(); };
    // Row mapping (edit mode): connect trees into rows; Undo steps back a point, then the history.
    document.getElementById('dpRowBtn').onclick = () => { if (rowMode) exitRowMode(); else enterRowMode(); };
    const rowDone = document.getElementById('dpRowDoneBtn');
    if (rowDone) rowDone.onclick = () => { if (rowMode) exitRowMode(); };
    document.getElementById('dpRowFinishBtn').onclick = finishRow;
    document.getElementById('dpRowUndoBtn').onclick = () => { if (rowDraft.length) rowUndoPoint(); else doUndo(); };
    document.getElementById('dpTreeRemoveBtn').onclick = toggleTreeRemoved;
    document.getElementById('dpTreeDeleteBtn').onclick = deleteTree;
    document.getElementById('dpTreeUndoBtn').onclick = doUndo;
    document.getElementById('dpSectionsToggle').onclick = () => {
        sectionsVisible = !sectionsVisible;
        // The layer toggle closes the road picker; the edit tools follow visibility (syncModeUI).
        splitSetupOpen = false;
        applySectionsVisibility();
    };
    document.getElementById('dpRowsToggle').onclick = () => { rowsVisible = !rowsVisible; applyRowsVisibility(); };
    // Download PDF: hand the current block + its (live) sub-blocks to the export module.
    const pdfBtn = document.getElementById('dpPdfBtn');
    if (pdfBtn) pdfBtn.onclick = () => {
        if (typeof BlockPdf === 'undefined') { Toast.show('PDF module not loaded.', 'error'); return; }
        BlockPdf.open({
            feature,
            subblocks: (workFC && workFC.features) ? workFC.features : [],
            buffer_meters: feature.buffer_meters || 0,
            rows: rowsForPdf(),
        });
    };
    document.getElementById('dpBrushSlider').oninput = function () {
        brushM = parseFloat(this.value); document.getElementById('dpBrushVal').textContent = brushM + 'm';
        if (brushCursor) brushCursor.setRadius(brushM);
    };
}

// ── Trees (manual tree management) ────────────────────────────────────────
// Trees live in the database (imported from the survey by `manage.py import_trees`, the same way
// blocks are). `treeBase` is the block's saved trees straight from the API, keyed by their row id;
// `treeEdits` holds the unsaved changes keyed by tree, and is what Save sends.
let treeMode = false;
let treeRenderer = null;          // interactive canvas in the 'trees' pane (created in initBlockMap)
let treeLayer = null;             // L.layerGroup of the rendered markers
const treeMarkers = {};           // key -> circleMarker
let treeBase = null;              // [{key, ref, id, lat, lng, category, source, status}] or null (not loaded)
let treeBaseByKey = {};
let treeEdits = {};               // key -> effective tree object (unsaved)
let treeSelected = null;          // selected key
let treeSeq = 1;                  // n:<seq> keys for trees added in this session
let treeLoading = null;           // in-flight load promise (per block)
let treeImported = true;          // false when this block has no trees in the DB yet
let treeCategoryPicked = false;   // category menu was explicitly chosen; next existing tree may be reclassified
let treeCategorySync = null;      // set by initTreeCategoryPicker(): mirror a category into the picker without applying it

// Small colour dot so the picker shows the same colour the tree takes on the map.
function treeSwatch(color) {
    return `<span class="inline-block h-2.5 w-2.5 rounded-full shrink-0" style="background:${color}"></span>`;
}

function treeDirty() { return Object.keys(treeEdits).length > 0; }
function treeRadius(selected) { return Math.max(3, Math.min(7, blockmap.getZoom() - 12)) + (selected ? 3 : 0); }
function treeEffective(key) { return treeEdits[key] || treeBaseByKey[key] || null; }
function treeList() {   // effective trees: base with edits applied + new (unsaved) trees
    const out = treeBase ? treeBase.map(t => treeEdits[t.key] || t) : [];
    Object.values(treeEdits).forEach(e => { if (!treeBaseByKey[e.key]) out.push(e); });
    return out;
}


function resetTreeState() {
    if (treeMode) exitTreeMode();
    resetRowState();
    treeBase = null; treeBaseByKey = {}; treeEdits = {}; treeSelected = null; treeLoading = null;
}

// The block's trees, straight from the database — one small request per block (no more
// downloading the estate-wide palm GeoJSON in the browser).
function loadTreeBase(feature) {
    if (treeBase) return Promise.resolve(treeBase);
    if (treeLoading) return treeLoading;
    treeLoading = $.get('/api/block/tree/list', { block_id: feature.id }).then(res => {
        treeBase = ((res && res.trees) || []).map(t => ({
            key: t.id, id: t.id, lat: t.lat, lng: t.lng, height_m: t.height_m,
            category: t.category, source: t.source, status: t.status,
        }));
        treeBaseByKey = {}; treeBase.forEach(t => { treeBaseByKey[t.key] = t; });
        treeImported = !!(res && res.imported);
        if (secBlockFeature && res) secBlockFeature.tree_counts = res.counts || {};
        treeLoading = null;
        return treeBase;
    }).catch(err => { treeLoading = null; Toast.show('Could not load trees: ' + (err?.responseJSON?.error || 'failed'), 'error'); throw err; });
    return treeLoading;
}

// Shown when a block's trees were never imported — the map has nothing to manage yet.
const NOT_IMPORTED_HINT = '⚠️ No trees in the database for this block — run “manage.py import_trees”.';

function enterTreeMode() {
    if (treeMode || !secBlockFeature) return;
    endCut(); endErase(); endPen(); exitRowMode();
    clearPalmLayers();                       // the tree layer shows every palm, so the per-category layers would duplicate it
    treeMode = true; treeSelected = null; treeCategoryPicked = false;
    blockmap._container.style.cursor = 'crosshair';
    blockmap.doubleClickZoom.disable();
    updateSectionUI();
    setTreeHint('Loading trees…');
    loadTreeBase(secBlockFeature).then(() => {
        if (!treeMode) return;
        renderTrees(); refreshInventory(); updateTreeUI();
        setTreeHint(treeImported ? 'Click inside the block to add a tree · click a tree to select it.' : NOT_IMPORTED_HINT);
    }).catch(() => exitTreeMode());
}
function exitTreeMode() {
    if (!treeMode) return;
    treeMode = false; treeSelected = null; treeCategoryPicked = false;
    if (blockmap) { blockmap._container.style.cursor = ''; blockmap.doubleClickZoom.enable(); }
    renderTrees();                           // removes the layer (only rendered while tree mode is on)
    updateSectionUI();
}

function treeStyle(t, selected, rowColor) {
    const cfg = PALM_CONFIG[t.category] || PALM_CONFIG.palm_normal;
    const st = { renderer: treeRenderer, radius: treeRadius(selected), fillColor: cfg.color, fillOpacity: 1, color: '#fff', weight: 1 };
    
    // Highlight the tree with the row's color on the border, but keep its natural fill color
    if (rowColor) { st.color = rowColor; st.weight = 3; st.radius = treeRadius(selected) + 2; st.fillOpacity = 0.9; }
    
    if (t.source === 'manual') { st.color = '#111827'; st.weight = 1.5; }                     // added by hand: dark ring
    if (t.status === 'removed') { st.fillColor = '#9ca3af'; st.color = '#ef4444'; st.weight = 1.5; st.fillOpacity = 0.9; }  // pruned: grey + red ring
    if (selected) { st.color = '#2563eb'; st.weight = 3; }
    return st;
}
function treeTip(t) {
    const cfg = PALM_CONFIG[t.category] || PALM_CONFIG.palm_normal;
    return `${cfg.emoji} ${cfg.label}` + (t.source === 'manual' ? ' · added' : '') + (t.status === 'removed' ? ' · removed' : '') + (!t.id && t.source === 'manual' ? ' (unsaved)' : '');
}

function renderTrees() {
    if (treeLayer) { blockmap.removeLayer(treeLayer); treeLayer = null; }
    Object.keys(treeMarkers).forEach(k => delete treeMarkers[k]);
    if (!(treeMode || rowMode || rowsVisible) || !treeBase) return;   // row mapping/visibility shows the tree layer
    reconcileRowTreeIds();
    const rowColors = (rowMode || rowsVisible) ? rowColorMap() : null;
    treeLayer = L.layerGroup();
    treeList().forEach(t => {
        if (t.status === 'deleted') return;
        const color = rowColors && rowColors[treePtKey(t)];
        if (rowsVisible && !rowMode && !treeMode && !color) return; // Only show trees in a row when just viewing rows
        const m = L.circleMarker([t.lat, t.lng], treeStyle(t, t.key === treeSelected, color));
        // Lazy tooltip: only bind once on first hover so that creating thousands of
        // markers on load does not build thousands of tooltip DOM nodes upfront.
        m.on('mouseover', function () {
            if (!this.getTooltip()) this.bindTooltip(treeTip(treeEffective(t.key) || t), { sticky: true, className: 'nh-hover-tip' }).openTooltip();
        });
        m.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            if (rowMode) rowAddTree(t);
            // Clicking a tree always just selects it — the picker then mirrors that tree's
            // status, and changing the picker is what reclassifies it.
            else if (treeMode) selectTree(t.key === treeSelected ? null : t.key);
        });
        treeMarkers[t.key] = m;
        treeLayer.addLayer(m);
    });
    treeLayer.addTo(blockmap);
}

// Cheap style-only refresh — NO layer rebuild. Works like the Dashboard approach:
// update setStyle() on existing markers and refresh row lines, without touching the DOM.
// Use this for row clicks, tree selection, add/remove, undo — anything that does NOT
// add or delete a marker. Full renderTrees() is only needed on mode enter/exit and after save.
function updateTreeStyles() {
    if (!treeLayer || !treeBase) return;
    reconcileRowTreeIds();
    const rowColors = (rowMode || rowsVisible) ? rowColorMap() : null;
    treeList().forEach(t => {
        const m = treeMarkers[t.key];
        if (!m) return;   // new marker will be added separately (addTree() does it inline)
        const color = rowColors && rowColors[treePtKey(t)];
        m.setStyle(treeStyle(t, t.key === treeSelected, color));
        // Refresh an already-open tooltip so the status label stays current.
        const tt = m.getTooltip && m.getTooltip();
        if (tt) tt.setContent(treeTip(treeEffective(t.key) || t));
    });
}

function selectTree(key) {
    const prev = treeSelected; treeSelected = key;
    // Only restyle the two affected markers (previously selected + newly selected).
    [prev, key].forEach(k => { const m = k && treeMarkers[k], t = k && treeEffective(k); if (m && t) { m.setStyle(treeStyle(t, k === key)); if (k === key) m.bringToFront(); } });
    // Mirror the selected tree's current status into the picker so the panel shows what
    // is selected — and so changing the picker edits THAT tree.
    const sel = key && treeEffective(key);
    if (sel && treeCategorySync) treeCategorySync(sel.category);
    updateTreeUI();
}

// Record an edit; an edit identical to the saved state is dropped (nothing to send).
function setTreeEdit(key, obj) {
    const base = treeBaseByKey[key];
    if (base && base.status === obj.status && base.lat === obj.lat && base.lng === obj.lng && base.category === obj.category) delete treeEdits[key];
    else treeEdits[key] = obj;
}

// Ray-cast point-in-polygon on a block's GeoJSON (Polygon / MultiPolygon, holes respected).
function pointInBlock(latlng) { return pointInGeometry(latlng, secBlockFeature && secBlockFeature.geometry); }
function blockAt(latlng) { return (blocks.features || []).find(f => f.geometry && pointInGeometry(latlng, f.geometry)) || null; }
function pointInGeometry(latlng, g) {
    if (!g) return true;
    const x = latlng.lng, y = latlng.lat;
    const inRing = (ring) => { let inside = false;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
            if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) inside = !inside;
        } return inside; };
    const inPoly = (rings) => inRing(rings[0]) && !rings.slice(1).some(inRing);
    if (g.type === 'Polygon') return inPoly(g.coordinates);
    if (g.type === 'MultiPolygon') return g.coordinates.some(inPoly);
    return true;
}

function addTree(latlng) {
    if (!treeMode || !treeBase || spacePan) return;
    if (!pointInBlock(latlng)) { setTreeHint('⚠️ Click inside the block boundary to add a tree.'); return; }
    pushUndo(false);
    const key = 'n:' + (treeSeq++);
    treeEdits[key] = { key, id: null, lat: latlng.lat, lng: latlng.lng, source: 'manual', status: 'active',
                       category: document.getElementById('dpTreeCategory').value || 'palm_normal' };
    const t = treeEdits[key];
    const m = L.circleMarker([t.lat, t.lng], treeStyle(t, false));
    m.bindTooltip(treeTip(t), { sticky: true, className: 'nh-hover-tip' });
    m.on('click', (e) => { L.DomEvent.stopPropagation(e); selectTree(t.key === treeSelected ? null : t.key); });
    treeMarkers[key] = m; treeLayer.addLayer(m);
    setTreeHint('🌴 Tree added — click Save to persist.');
    refreshInventory(); updateSectionUI();
}

// Reclassify the selected tree straight from the picker. No confirm dialog: the change is
// immediate and visible (marker recolours), and Undo covers a mis-click.
function applyTreeCategory(key, category) {
    const tree = treeEffective(key);
    const next = PALM_CONFIG[category];
    const current = tree && PALM_CONFIG[tree.category];
    if (!tree || !next || !current || tree.category === category) return;
    pushUndo(false);
    setTreeEdit(tree.key, { ...tree, category: category });
    // Restyle just this marker + refresh its tooltip; no full layer rebuild.
    const eff = treeEffective(tree.key), m = treeMarkers[tree.key];
    if (m) { m.setStyle(treeStyle(eff, true)); const tt = m.getTooltip && m.getTooltip(); if (tt) tt.setContent(treeTip(eff)); }
    setTreeHint(`${next.emoji} Changed from ${current.label} to ${next.label}.`);
    refreshInventory(); updateSectionUI(); updateTreeUI();
}

// Mark the selected tree as pruned/removed (or restore it if it already is).
function toggleTreeRemoved() {
    const t = treeSelected && treeEffective(treeSelected); if (!t) return;
    pushUndo(false);
    setTreeEdit(t.key, { ...t, status: t.status === 'removed' ? 'active' : 'removed' });
    // Restyle only this one marker — no full layer rebuild needed.
    const eff = treeEffective(t.key), m = treeMarkers[t.key];
    if (m) { m.setStyle(treeStyle(eff, true)); const tt = m.getTooltip && m.getTooltip(); if (tt) tt.setContent(treeTip(eff)); }
    setTreeHint(eff.status === 'removed' ? '✂️ Marked as pruned / removed.' : '↩️ Tree restored.');
    refreshInventory(); updateSectionUI();
}

function deleteTree() {
    const t = treeSelected && treeEffective(treeSelected); if (!t) return;
    pushUndo(false);
    if (t.source === 'manual' && !t.id) delete treeEdits[t.key];                 // never saved → just forget it
    else setTreeEdit(t.key, { ...t, status: 'deleted' });
    const m = treeMarkers[t.key]; if (m) { treeLayer.removeLayer(m); delete treeMarkers[t.key]; }
    treeSelected = null;
    setTreeHint('🗑️ Tree deleted.');
    refreshInventory(); updateSectionUI();
}

function setTreeHint(text) { const h = document.getElementById('dpTreeHint'); if (h) h.textContent = text; }

// A native <select> lets the browser decide where its option popup goes. Keep this menu anchored
// directly below its field without moving the surrounding Manage trees controls.
function initTreeCategoryPicker() {
    const input = document.getElementById('dpTreeCategory');
    const button = document.getElementById('dpTreeCategoryBtn');
    const label = document.getElementById('dpTreeCategoryLabel');
    const options = document.getElementById('dpTreeCategoryOptions');
    const picker = document.getElementById('dpTreeCategoryPicker');
    if (!input || !button || !label || !options || !picker) return;
    // `explicit` = the user chose from the menu (vs. the picker mirroring a selected tree).
    // With a tree selected, an explicit choice reclassifies THAT tree; with nothing selected
    // it sets the category for the next tree added.
    const setCategory = (name, explicit = false) => {
        const cfg = PALM_CONFIG[name] || PALM_CONFIG.palm_normal;
        input.value = PALM_CONFIG[name] ? name : 'palm_normal';
        label.innerHTML = `${treeSwatch(cfg.color)}<span class="truncate">${cfg.emoji} ${cfg.label}</span>`;
        options.classList.add('hidden');
        button.setAttribute('aria-expanded', 'false');
        if (!explicit) return;
        treeCategoryPicked = true;
        // A tree is selected → apply the new status to it right away.
        const sel = treeSelected && treeEffective(treeSelected);
        if (sel && treeMode) { applyTreeCategory(sel.key, input.value); return; }
        if (treeMode) setTreeHint(`${cfg.emoji} ${cfg.label} selected — click an empty spot to add one, or click a tree to change it.`);
    };
    // Exposed so selectTree() can mirror the selected tree's status into the picker.
    treeCategorySync = (name) => setCategory(name, false);
    options.innerHTML = Object.keys(PALM_CONFIG).map(name => {
        const cfg = PALM_CONFIG[name];
        return `<button type="button" data-tree-category="${name}" class="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left text-slate-700 hover:bg-emerald-50">${treeSwatch(cfg.color)}<span>${cfg.emoji} ${cfg.label}</span></button>`;
    }).join('');
    setCategory(input.value);
    button.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = options.classList.toggle('hidden');
        button.setAttribute('aria-expanded', String(!open));
    });
    options.addEventListener('click', (e) => {
        const option = e.target.closest('[data-tree-category]');
        if (!option) return;
        e.stopPropagation();
        setCategory(option.dataset.treeCategory, true);
    });
    document.addEventListener('click', (e) => {
        if (!picker.contains(e.target)) {
            options.classList.add('hidden');
            button.setAttribute('aria-expanded', 'false');
        }
    });
}

function updateTreeUI() {
    const btn = document.getElementById('dpTreeBtn'); if (!btn) return;
    document.getElementById('dpTreeBtnLabel').textContent = 'Start managing trees';
    $('#dpTreeBtn').toggle(!treeMode);
    $('#dpTreeControls').toggle(treeMode);
    const sel = treeSelected && treeEffective(treeSelected);
    // The picker does double duty: with a tree selected it edits that tree, otherwise it
    // sets the status for the next tree added. Say which, so the label is never misleading.
    const title = document.getElementById('dpTreeCategoryTitle');
    if (title) title.textContent = sel ? 'Tree status (selected tree)' : 'Tree status (new tree)';
    const rm = document.getElementById('dpTreeRemoveBtn'), del = document.getElementById('dpTreeDeleteBtn');
    rm.disabled = !sel; del.disabled = !sel;
    rm.innerHTML = sel && sel.status === 'removed' ? '↩️ Restore' : '✂️ Mark removed';
    document.getElementById('dpTreeUndoBtn').disabled = !undoStack.length;
    const info = document.getElementById('dpTreeInfo');
    if (info && secBlockFeature) {
        const c = effectiveTreeCounts(secBlockFeature);
        let txt = `🌴 ${fmtInt(c.__total)} trees`;
        if (c.__removed) txt += ` · ${c.__removed} removed`;
        info.innerHTML = txt + (treeDirty() ? ' <span style="color:#f59e0b">(unsaved)</span>' : '');
    }
}

function saveTrees(onDone, onFail) {
    const edits = Object.values(treeEdits)
        .filter(e => !(e.source === 'manual' && !e.id && e.status === 'deleted'))
        .map(e => ({ id: e.id || '', category: e.category, status: e.status, lat: e.lat, lng: e.lng }));
    $.ajax({
        type: 'POST', url: '/api/block/tree/save',
        data: { block_id: secBlockId, edits: JSON.stringify(edits) },
        headers: { 'X-CSRFToken': csrf() },
        success: function (res) {
            // Rebuild the saved base from the server's answer: overrides/ids now exist for the edited trees.
            treeEdits = {}; treeSelected = null; undoStack = [];
            if (secBlockFeature) secBlockFeature.tree_counts = res.counts || {};
            const wasBase = treeBase; treeBase = null; treeBaseByKey = {};
            const finish = () => { reconcileRowTreeIds(); renderRows(); renderTrees(); refreshInventory(); updateSectionUI(); Toast.show(res.message || 'Tree changes saved.', 'success'); if (onDone) onDone(); };
            if (wasBase && secBlockFeature) loadTreeBase(secBlockFeature).then(finish).catch(() => { if (onDone) onDone(); });
            else finish();
        },
        error: function (xhr) { Toast.show('Save trees: ' + (xhr.responseJSON?.error || 'failed'), 'error'); if (onFail) onFail(); }
    });
}

// ── Row mapping (connect trees into plantation rows: R1, R2, …) ───────────
// A row is an ordered chain of tree points, stored by coordinate (+ the survey ref when the
// point is a survey palm) so it survives re-splits and tree-id changes. Rows alternate two
// colours so neighbouring rows stay readable, and carry a permanent "R1 (12)" map label.
const ROW_PALETTE = ['#2563eb', '#f97316'];   // blue / orange, alternating by row order
let rowMode = false;
let rows = [];                 // working set: [{id, label, trees:[{ref,lat,lng}]}]
let rowsSavedSig = '[]';       // signature of the last saved set (dirty check)
let rowDraft = [];             // the chain currently being connected
let rowsVisible = false;       // "Show rows" toggle — draws the saved rows outside the editor too
let rowsLoadedFor = null;      // block id whose rows are in `rows` (avoids re-fetching)
let rowLayer = null;           // polylines + labels
let rowLoading = null;

function rowSig(list) { return JSON.stringify((list || []).map(r => [r.label, (r.trees || []).map(p => [p.lat, p.lng])])); }
function rowsDirty() { return rowSig(rows) !== rowsSavedSig; }
function rowColor(i) { return ROW_PALETTE[i % ROW_PALETTE.length]; }
// One key per physical tree: its database id, or its coordinates while it is still unsaved.
function ptKey(p) { return p.id ? 't:' + p.id : 'c:' + Number(p.lat).toFixed(7) + ',' + Number(p.lng).toFixed(7); }
function treePtKey(t) { return t.id ? 't:' + t.id : 'c:' + Number(t.lat).toFixed(7) + ',' + Number(t.lng).toFixed(7); }

// After trees are saved, rows that referenced a then-unsaved tree pick up its new id.
function reconcileRowTreeIds() {
    if (!treeBase) return;
    const byCoord = {};
    treeBase.forEach(t => { byCoord['c:' + Number(t.lat).toFixed(7) + ',' + Number(t.lng).toFixed(7)] = t.id; });
    [...rows.map(r => r.trees || []), rowDraft].forEach(pts => pts.forEach(p => {
        if (!p.id) { const id = byCoord['c:' + Number(p.lat).toFixed(7) + ',' + Number(p.lng).toFixed(7)]; if (id) p.id = id; }
    }));
}
// Rows store only {id, lat, lng}. The PDF needs each tree's category so it can draw the
// tree in its own colour with the row's colour on the outline, the way treeStyle() does
// on the map — so enrich them from `treeBase` on the way out.
function rowsForPdf() {
    if (typeof rows === 'undefined') return [];
    const byKey = {};
    (treeBase || []).forEach(t => { byKey[ptKey(t)] = t; });
    return rows.map(r => Object.assign({}, r, {
        trees: (r.trees || []).map(p => {
            const t = byKey[ptKey(p)];
            return t ? Object.assign({}, p, { category: t.category }) : p;
        }),
    }));
}

function rowColorMap() {
    const map = {};
    rows.forEach((r, i) => (r.trees || []).forEach(p => { map[ptKey(p)] = rowColor(i); }));
    rowDraft.forEach(p => { map[ptKey(p)] = rowColor(rows.length); });   // draft previews its future colour
    return map;
}

function resetRowState() {
    if (rowMode) exitRowMode();
    rows = []; rowDraft = []; rowsSavedSig = '[]'; rowLoading = null; rowsLoadedFor = null;
    document.getElementById('dpRowsToggleRow').classList.add('hidden');   // `rowsVisible` itself is sticky: it's the user's preference
}

// Load the block's rows when its detail panel opens, so "Show rows" works without the editor.
function loadBlockRows(blockId) {
    loadRows(blockId).then(applyRowsVisibility).catch(() => {});
}

// Show / hide the saved rows (toggle). The toggle row only appears when the block has rows.
function applyRowsVisibility() {
    const toggle = document.getElementById('dpRowsToggle');
    if (!toggle) return;
    document.getElementById('dpRowsToggleRow').classList.toggle('hidden', rows.length === 0);
    toggle.classList.toggle('on', rowsVisible);
    renderRows();
    if (rowsVisible && secBlockFeature && !treeBase) {
        loadTreeBase(secBlockFeature).then(() => { renderTrees(); });
    } else {
        renderTrees();
    }
    updateRowUI();
}

function loadRows(blockId) {
    if (rowLoading) return rowLoading;
    rowLoading = $.get('/api/block/row/list', { block_id: blockId }).then(res => {
        rows = (res && res.rows) || [];
        rowsSavedSig = rowSig(rows);
        rowsLoadedFor = blockId;
        rowLoading = null;
        return rows;
    }).catch(err => { rowLoading = null; Toast.show('Could not load rows: ' + (err?.responseJSON?.error || 'failed'), 'error'); throw err; });
    return rowLoading;
}

function enterRowMode() {
    if (rowMode || !secBlockFeature) return;
    endCut(); endErase(); endPen(); exitTreeMode();
    clearPalmLayers();                    // the tree layer already shows every palm
    rowMode = true; rowDraft = [];
    blockmap._container.style.cursor = 'crosshair';
    blockmap.doubleClickZoom.disable();
    updateSectionUI();
    setRowHint('Loading trees…');
    Promise.all([loadTreeBase(secBlockFeature),
                 (rowsDirty() || rowsLoadedFor === secBlockId) ? Promise.resolve(rows) : loadRows(secBlockId)])
        .then(() => {
            if (!rowMode) return;
            renderRows(); renderTrees(); updateSectionUI();   // full build on first load into row mode
            setRowHint(treeImported ? 'Click trees one by one to connect them, then “Finish row”.' : NOT_IMPORTED_HINT);
        })
        .catch(() => exitRowMode());
}

function exitRowMode() {
    if (!rowMode) return;
    rowMode = false; rowDraft = [];
    if (blockmap) { blockmap._container.style.cursor = ''; blockmap.doubleClickZoom.enable(); }
    renderRows();      // removes the row polylines (only drawn while mapping)
    renderTrees();     // removes the tree layer (only rendered while tree mode / row mode is on)
    updateSectionUI();
}

function setRowHint(text) { const h = document.getElementById('dpRowHint'); if (h) h.textContent = text; }

// Click a tree: append it to the chain (clicking the last one again removes it).
function rowAddTree(t) {
    if (!rowMode) return;
    const key = treePtKey(t);
    if (rowDraft.length && ptKey(rowDraft[rowDraft.length - 1]) === key) { rowUndoPoint(); return; }
    if (rowDraft.some(p => ptKey(p) === key)) { setRowHint('⚠️ That tree is already in this row.'); return; }
    const inRow = rows.findIndex(r => (r.trees || []).some(p => ptKey(p) === key));
    if (inRow >= 0) { setRowHint(`⚠️ That tree already belongs to ${rows[inRow].label}.`); return; }
    pushUndo(false);
    rowDraft.push({ id: t.id || null, lat: t.lat, lng: t.lng });
    renderRows(); updateTreeStyles();   // cheap: restyle only, no layer rebuild
    setRowHint(rowDraft.length < 2 ? 'Click the next tree in the row…'
                                   : `${rowDraft.length} trees connected — click “Finish row” to name it.`);
    updateSectionUI();
}

function rowUndoPoint() {
    if (!rowDraft.length) return;
    rowDraft.pop();
    renderRows(); updateTreeStyles();   // cheap: restyle only, no layer rebuild
    setRowHint(rowDraft.length ? `${rowDraft.length} tree(s) connected.` : 'Click trees one by one to connect them.');
    updateSectionUI();
}

function clearRowDraft() {
    if (!rowDraft.length) return;
    rowDraft = [];
    renderRows(); updateTreeStyles();   // cheap: restyle only, no layer rebuild
    setRowHint('Row cancelled. Click trees to start a new one.');
    updateSectionUI();
}

function finishRow() {
    if (rowDraft.length < 2) { Toast.show('Connect at least two trees to make a row.', 'error'); return; }
    pushUndo(false);
    rows.push({ id: null, label: nextRowLabel(), trees: rowDraft.slice() });
    rowDraft = [];
    renderRows(); updateTreeStyles();   // cheap: update colours for newly-assigned row, no rebuild
    setRowHint('Row added — click Save to persist. Start the next row by clicking a tree.');
    updateSectionUI();
}

// R1, R2, … — the first number not already taken by an existing label.
function nextRowLabel() {
    const used = new Set(rows.map(r => r.label));
    let n = 1;
    while (used.has('R' + n)) n++;
    return 'R' + n;
}

function deleteRow(i) {
    if (!rows[i]) return;
    pushUndo(false);
    rows.splice(i, 1);
    renderRows(); updateTreeStyles();   // cheap: un-colour freed trees, no layer rebuild
    setRowHint('Row deleted — click Save to persist.');
    updateSectionUI();
}

function renameRow(i, label) {
    label = (label || '').trim();
    if (!rows[i] || !label) { Toast.show('Row label is required.', 'error'); return; }
    pushUndo(false);
    rows[i].label = label;
    renderRows();
    updateSectionUI();
}

function renderRows() {
    if (rowLayer) { blockmap.removeLayer(rowLayer); rowLayer = null; }
    // Drawn while mapping rows, and outside the editor whenever "Show rows" is on.
    if (!rowMode && !(rowsVisible && rows.length)) { renderRowList(); return; }
    rowLayer = L.layerGroup();
    rows.forEach((r, i) => {
        const latlngs = (r.trees || []).map(p => [p.lat, p.lng]);
        if (latlngs.length < 2) return;
        // Outside the editor the lines are non-interactive so block clicks still land on the map.
        const line = L.polyline(latlngs, { color: rowColor(i), weight: 3, opacity: 0.95, interactive: rowMode });
        // Permanent "R1 (🌴 12)" label, same tooltip style as the sub-block names.
        line.bindTooltip(`${r.label} (🌴 ${latlngs.length})`, { permanent: true, direction: 'center', className: 'leaflet-tooltip' });
        if (rowMode) line.on('click', (e) => L.DomEvent.stopPropagation(e));
        rowLayer.addLayer(line);
    });
    if (rowDraft.length >= 2) {
        rowLayer.addLayer(L.polyline(rowDraft.map(p => [p.lat, p.lng]),
            { color: rowColor(rows.length), weight: 3, dashArray: '5 4', opacity: 0.9 }));
    }
    rowLayer.addTo(blockmap);
    renderRowList();
}

// Panel list: one line per row (colour chip + label + tree count), with rename / delete.
function renderRowList() {
    const wrap = document.getElementById('dpRowList');
    if (!wrap) return;
    if (!rows.length) { wrap.innerHTML = '<p class="text-[11px] text-slate-400">No rows yet.</p>'; return; }
    wrap.innerHTML = rows.map((r, i) => {
        const n = (r.trees || []).length;
        const label = (r.label || '').replace(/"/g, '&quot;');
        return `<div class="row-item rounded-lg border border-slate-200 bg-white p-2.5" data-idx="${i}">
            <div class="flex items-center gap-2">
              <span class="h-6 w-6 rounded-md flex items-center justify-center text-[11px] font-bold text-white shrink-0" style="background:${rowColor(i)}">${String(i + 1).padStart(2, '0')}</span>
              <span class="row-label text-sm font-semibold text-slate-800 flex-1 min-w-0 truncate">${r.label}</span>
              ${r.id ? '' : '<span class="text-[10px] font-medium text-amber-500">unsaved</span>'}
              <button type="button" class="row-rename text-slate-300 hover:text-blue-600 text-sm leading-none shrink-0" title="Rename"><i class="bi bi-pencil"></i></button>
              <button type="button" class="row-delete text-slate-300 hover:text-red-600 text-sm leading-none shrink-0" title="Delete row"><i class="bi bi-trash"></i></button>
            </div>
            <div class="mt-1.5 pl-8 flex items-center gap-2 text-xs text-slate-500">
              <span class="inline-flex items-center gap-1">🌴 <b class="text-slate-700">${fmtInt(n)}</b> trees</span>
            </div>
            <div class="row-edit hidden mt-2 items-center gap-1.5">
              <input class="row-input flex-1 min-w-0 border border-slate-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" value="${label}">
              <button type="button" class="row-save inline-flex items-center justify-center h-7 w-7 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-sm leading-none shrink-0" title="Save"><i class="bi bi-check-lg"></i></button>
              <button type="button" class="row-cancel inline-flex items-center justify-center h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm leading-none shrink-0" title="Cancel"><i class="bi bi-x-lg"></i></button>
            </div>
          </div>`;
    }).join('');
}

// Row list interactions (delegated; attach once) — same inline-rename pattern as the sub-blocks.
$(document).on('click', '#dpRowList .row-rename', function () {
    const ed = this.closest('.row-item').querySelector('.row-edit');
    ed.classList.remove('hidden'); ed.classList.add('flex');
    const inp = ed.querySelector('.row-input'); inp.focus(); inp.select();
});
$(document).on('click', '#dpRowList .row-cancel', function () {
    const ed = this.closest('.row-item').querySelector('.row-edit');
    ed.classList.add('hidden'); ed.classList.remove('flex');
});
$(document).on('click', '#dpRowList .row-save', function () {
    const item = this.closest('.row-item');
    renameRow(parseInt(item.getAttribute('data-idx'), 10), item.querySelector('.row-input').value);
});
$(document).on('keydown', '#dpRowList .row-input', function (e) {
    if (e.key === 'Enter') { e.preventDefault(); this.closest('.row-item').querySelector('.row-save').click(); }
    if (e.key === 'Escape') { this.closest('.row-item').querySelector('.row-cancel').click(); }
});
$(document).on('click', '#dpRowList .row-delete', function () {
    deleteRow(parseInt(this.closest('.row-item').getAttribute('data-idx'), 10));
});

function updateRowUI() {
    const btn = document.getElementById('dpRowBtn'); if (!btn) return;
    document.getElementById('dpRowBtnLabel').textContent = 'Start mapping rows';
    $('#dpRowBtn').toggle(!rowMode);
    $('#dpRowControls').toggle(rowMode);
    document.getElementById('dpRowFinishBtn').disabled = rowDraft.length < 2;
    document.getElementById('dpRowUndoBtn').disabled = !rowDraft.length && !undoStack.length;
    document.getElementById('dpRowUndoBtn').innerHTML = rowDraft.length
        ? '<i class="bi bi-arrow-counterclockwise"></i> Undo point'
        : '<i class="bi bi-arrow-counterclockwise"></i> Undo';
    const info = document.getElementById('dpRowInfo');
    if (info) {
        const trees = rows.reduce((a, r) => a + (r.trees || []).length, 0);
        info.innerHTML = rows.length
            ? `${rows.length} rows · 🌴 ${fmtInt(trees)} trees mapped` + (rowsDirty() ? ' <span style="color:#f59e0b">(unsaved)</span>' : ' <span style="color:#22c55e">(saved)</span>')
            : 'No rows mapped yet.';
    }
}

function saveRows(onDone, onFail) {
    $.ajax({
        type: 'POST', url: '/api/block/row/save',
        data: { block_id: secBlockId, rows: JSON.stringify(rows.map(r => ({ id: r.id || '', label: r.label, trees: r.trees }))) },
        headers: { 'X-CSRFToken': csrf() },
        success: function (res) {
            rows = (res && res.rows) || [];
            rowsSavedSig = rowSig(rows);
            rowsLoadedFor = secBlockId;
            applyRowsVisibility(); renderTrees(); updateSectionUI();
            Toast.show(res.message || 'Rows saved.', 'success');
            if (onDone) onDone();
        },
        error: function (xhr) { Toast.show('Save rows: ' + (xhr.responseJSON?.error || 'failed'), 'error'); if (onFail) onFail(); }
    });
}

// ── Full-screen editor (view mode ↔ edit mode) ─────────────
// The SAME map card is pinned over the viewport (CSS: body.nh-block-fullscreen), so every
// layer, the detail panel and the section editor keep working untouched. Only which panel
// controls are visible changes: view mode shows "Edit"; edit mode shows Split / tools / Save.
let _dpRenderedEditing = null;   // which mode #dpContent was last built for

function syncDetailMode() {
    if (!secBlockFeature || _dpRenderedEditing === editing) return;
    _dpRenderedEditing = editing;
    document.getElementById('dpContent').innerHTML = buildDetailHTML(secBlockFeature);
    wirePalmToggles();
}

function syncModeUI() {
    const hasFeats = !!(workFC && workFC.features.length);
    const canEditSections = editing && hasFeats && sectionsVisible;
    document.body.classList.toggle('nh-block-fullscreen', editing);
    $('#mapTitle').text(editing ? 'Edit sub-blocks' : 'Estate Map');
    // jQuery .toggle() sets inline display so Tailwind display utilities can't override it.
    $('#mapEditBtn').toggle(!editing);
    $('#mapExitBtn').toggle(editing);
    $('#dpPdfBtn').toggle(!editing);
    // In the editor the Merge/Cut/Pen/Erase tools are the Sub-blocks card's default view.
    // Re-split is a reset step: it swaps the tools for the road picker + Preview + Done.
    const showSplitSetup = editing && splitSetupOpen;
    $('#dpSplitBtn').toggle(editing);
    $('#dpSplitControls').toggle(showSplitSetup);
    $('#dpEditFooter').toggle(editing);
    $('#dpEditControls').toggle(canEditSections && !splitSetupOpen);
    $('#dpEditGroup').toggle(editing);
    $('#dpTreeSection').toggle(editing);
    $('#dpRowSection').toggle(editing);
    if (!editing && treeMode) exitTreeMode();   // leaving full screen ends tree placement (edits stay pending)
    if (!editing && rowMode) exitRowMode();
    if (!editing) $('#dpSplitControls').addClass('hidden');
    syncDetailMode();
}

// Fold every tool section away (road picker collapsed, tree placement and row mapping off).
function collapseTools() {
    if (treeMode) exitTreeMode();
    if (rowMode) exitRowMode();
    ['dpAccSubBody', 'dpAccTreeBody', 'dpAccRowBody'].forEach(closeAcc);
    const rc = document.getElementById('dpSplitControls');
    if (rc) rc.classList.add('hidden');
    splitSetupOpen = false;
}

// Re-split's Cancel: if Preview replaced the sub-blocks, put back the set (and its saved /
// unsaved state) from when Re-split was opened; then close the road picker.
function cancelResplit() {
    const snap = resplitSnap;
    resplitSnap = null;
    if (snap && snap.previewed) {
        clearSectionLayer();
        workFC = snap.feats ? { type: 'FeatureCollection', features: JSON.parse(snap.feats) } : null;
        dirty = snap.dirty;
        sectionsVisible = snap.visible;
        const hasFeats = !!(workFC && workFC.features.length);
        document.getElementById('dpSectionsToggleRow').classList.toggle('hidden', !hasFeats);
        document.getElementById('dpSectionsToggle').classList.toggle('on', sectionsVisible);
        document.getElementById('dpSectionInfo').innerHTML = snap.info;
        if (hasFeats && sectionsVisible) renderWork(); else updateSectionUI();
    }
    closeSplitWorkflow();
}

// Close only the split workflow. The full-screen map editor stays open, like Manage trees' Done.
function closeSplitWorkflow() {
    endCut(); endErase(); endPen();
    splitSetupOpen = false;
    syncModeUI();
}

function enterEditMode(pushUrl = true) {
    if (editing) return;
    editing = true;
    collapseTools();          // nothing is unfolded until its own button is clicked
    syncModeUI();
    syncUrl(pushUrl);
    // Let the fixed full-screen layout settle before Leaflet measures the container.
    setTimeout(refitMap, 50);
}

// Returns false when the user kept their unsaved edits (nothing changed).
function exitEditMode(pushUrl = true) {
    if (!editing) return true;
    if (!confirmDiscard()) return false;
    endCut(); endErase(); endPen();
    const discard = dirty || treeDirty() || rowsDirty();
    editing = false;
    syncModeUI();
    syncUrl(pushUrl);
    // The user chose to drop the unsaved working set → reload what is persisted.
    if (discard && secBlockId) loadSavedSections(secBlockId);
    setTimeout(refitMap, 50);
    return true;
}

// ── URL ↔ state: /block/all (view) · /block/all/edit (editor), ?block=<id> = selected block ──
// Kept in sync with pushState/replaceState so a refresh (or back/forward) restores the same view.
function urlFor() {
    const q = activeBlockId ? `?block=${encodeURIComponent(activeBlockId)}` : '';
    return (editing ? '/block/all/edit' : '/block/all') + q;
}
function syncUrl(push) {
    const url = urlFor();
    if (url === window.location.pathname + window.location.search) return;
    try { (push ? history.pushState : history.replaceState).call(history, { nh: true }, '', url); } catch (e) {}
}
// Apply the current URL (initial load and popstate): select ?block, then enter/leave the editor.
function applyUrlState() {
    const wantEdit = /\/edit\/?$/.test(window.location.pathname);
    const wantBlock = new URLSearchParams(window.location.search).get('block');
    if (wantBlock && String(wantBlock) !== String(activeBlockId)) {
        const feature = (blocks.features || []).find(f => String(f.id) === String(wantBlock));
        if (feature && layerByBlockId[feature.id]) selectBlock(feature, layerByBlockId[feature.id]);
    } else if (!wantBlock && activeLayer) closeDetail();
    if (wantEdit && !editing) enterEditMode(false);
    else if (!wantEdit && editing && !exitEditMode(false)) syncUrl(true);   // refused (unsaved) → put the editor URL back
}

function openDetail(feature) {
    const p = blockProps(feature);
    const code = feature.code || p.Block || '?';

    document.getElementById('dpBadge').textContent = code;
    document.getElementById('dpTitle').textContent = blockLabel(feature);
    document.getElementById('dpSubtitle').textContent =
        p['Planting Year'] ? `Planted ${p['Planting Year']}` : 'Plantation Block';
    document.getElementById('dpContent').innerHTML = buildDetailHTML(feature);
    _dpRenderedEditing = editing;

    wirePalmToggles();          // palm layer switches for the freshly-rendered content

    // NOTE: buffer distance display/editing is hidden for now (see buildDetailHTML).
    // The /api/block/update endpoint still accepts buffer_meters if it needs to come back.

    // sub-block panel: wire actions + load any saved sub-blocks for this block
    collapseTools();
    document.getElementById('dpSectionsToggleRow').classList.add('hidden');
    document.getElementById('dpSubblocks').classList.add('hidden');   // reset accordion until this block's sub-blocks load
    document.getElementById('dpSubblocksList').classList.add('hidden');  // start collapsed for each block
    (function () { const c = document.getElementById('dpSubblocksChev'); c.classList.remove('bi-chevron-down'); c.classList.add('bi-chevron-right'); })();
    hideViewSubblocks();   // view card: hidden + folded until this block's sub-blocks load
    _sbSig = null;
    document.getElementById('dpSaveBtn').disabled = true;
    wireSectionPanel(feature);
    loadSavedSections(feature.id);
    loadBlockRows(feature.id);      // rows power the "Show rows" toggle, editor or not
    syncModeUI();

    // slide the panel in
    document.getElementById('blockDetailPanel').classList.remove('translate-x-full');
}

// ── List interactions ─────────────────────────────────────
$(document).on('click', '.block-row', function () {
    const id = $(this).data('block-id');
    const feature = (blocks.features || []).find(f => String(f.id) === String(id));
    const layer = layerByBlockId[id];
    if (feature && layer) selectBlock(feature, layer);
});

// Resize palm dots on zoom
function bindZoom() {
    blockmap.on('zoomend', () => {
        const r = Math.max(3, Math.min(7, blockmap.getZoom() - 12));
        Object.values(activePalmLayers).forEach(l => l.eachLayer(m => m.setRadius(r)));
        Object.keys(treeMarkers).forEach(k => treeMarkers[k].setRadius(treeRadius(k === treeSelected)));
        // rescale road line weights to the new zoom, preserving the current focus dim
        applyRoadFocus(currentPalmCode);
        // rescale block border weights to the new zoom (active / dim / normal)
        if (blockLayer) blockLayer.eachLayer(l => {
            if (l === activeLayer)      l.setStyle({ weight: blockWeight(BLOCK_STYLE_ACTIVE.wmult) });
            else if (activeLayer)       l.setStyle({ weight: blockWeight(BLOCK_STYLE_DIM.wmult) });
            else                        l.setStyle({ weight: blockWeight(BLOCK_STYLE.wmult) });
        });
        updateTipVisibility();
    });
}

// Below the ortho tile min-zoom (14) the estate imagery is gone — hide block/road
// hover tooltips so the zoomed-out world view stays clean.
function updateTipVisibility() {
    if (blockmap) blockmap.getContainer().classList.toggle('nh-zoomed-out', blockmap.getZoom() < 14);
}

// ── Boot ──────────────────────────────────────────────────
$(document).ready(function () {
    if (typeof blocks === 'undefined' || !blocks.features) return;
    initBlockMap();
    bindZoom();

    // /block/all/edit?block=<id> → same page, straight into the editor on that block (survives refresh).
    if (typeof startInEditMode !== 'undefined' && startInEditMode && !/\/edit\/?$/.test(window.location.pathname)) {
        try { history.replaceState({ nh: true }, '', '/block/all/edit' + window.location.search); } catch (e) {}
    }
    applyUrlState();
    window.addEventListener('popstate', applyUrlState);

    const dpClose = document.getElementById('dpClose');
    if (dpClose) dpClose.addEventListener('click', closeDetail);

    // Tree management: keep the category options inside the editor panel.
    initTreeCategoryPicker();

    // Full-screen editor toggles in the map toolbar
    const mapEditBtn = document.getElementById('mapEditBtn');
    if (mapEditBtn) mapEditBtn.addEventListener('click', enterEditMode);
    const mapExitBtn = document.getElementById('mapExitBtn');
    if (mapExitBtn) mapExitBtn.addEventListener('click', exitEditMode);

    // Dropdown toggle
    const ddBtn = document.getElementById('blockDropdownBtn');
    if (ddBtn) ddBtn.addEventListener('click', (e) => { e.stopPropagation(); toggleBlockDropdown(); });

    // Close dropdown when clicking outside it
    document.addEventListener('click', (e) => {
        const dd = document.getElementById('blockDropdown');
        if (dd && !dd.contains(e.target)) closeBlockDropdown();
    });

    // Search filter
    $('#blockSearch').on('input', function () {
        const q = this.value.trim().toLowerCase();
        let visible = 0;
        $('.block-row').each(function () {
            const hay = ($(this).data('search') || '').toString();
            const match = !q || hay.indexOf(q) !== -1;
            $(this).toggle(match);
            if (match) visible++;
        });
        $('#blockListEmpty').toggleClass('hidden', visible !== 0);
    });

    // ── Settings (layers) dropdown ──
    const setBtn = document.getElementById('layerSettingsBtn');
    if (setBtn) setBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        document.getElementById('layerSettingsMenu').classList.toggle('hidden');
    });
    document.addEventListener('click', (e) => {
        const s = document.getElementById('layerSettings');
        if (s && !s.contains(e.target)) document.getElementById('layerSettingsMenu').classList.add('hidden');
    });
    // Each layer row toggle
    document.querySelectorAll('.layer-toggle[data-layer]').forEach(sw => {
        sw.addEventListener('click', function () {
            const name = this.dataset.layer;
            const on = !this.classList.contains('on');
            this.classList.toggle('on', on);
            toggleRoad(name, on);
        });
    });
});

}
